/* ===========================================================
   Opora Tools — Scope-of-Work Generator  v1.0.0
   Tool #10 in the Opora Supply Tools section.
   Wizard: 5 steps → PDF/text export.
   Global: window.OporaScopeGen
   Requires: opora-tools-core.js, jsPDF (CDN)
   Last reviewed: June 2026
   =========================================================== */

/* ===========================================================
   PURE LOGIC EXPORTS (also attached to OporaScopeGen.* for tests)
   =========================================================== */

function slugify(str) {
  if (!str || typeof str !== 'string') return 'facility';
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 60) || 'facility';
}

function sqftBucket(sqft) {
  var n = Number(sqft) || 0;
  if (n < 25000)  return 'small';
  if (n < 100000) return 'medium';
  if (n < 500000) return 'large';
  return 'x-large';
}

function defaultQualityLevel() {
  return 'APPA 2';
}

function appaDescription(level) {
  var levels = {
    'APPA 1': 'Orderly Spotlessness — highest standard; suitable for C-suite, critical care, or mission-critical areas. Near-zero defects; continuous inspection.',
    'APPA 2': 'Ordinary Tidiness — professional standard for most commercial and institutional facilities. Minor defects (dust, streaks) acceptable between service visits.',
    'APPA 3': 'Casual Inattention — cleaning occurs on schedule but defects may be present for extended periods. Appropriate for storage areas, back-of-house, light industrial.',
    'APPA 4': 'Moderate Dinginess — cleaning frequency reduced; some litter and dust acceptable. Suitable for outdoor structures, parking, non-public areas.',
    'APPA 5': 'Unkempt Neglect — cleaning only to avoid safety or health citations. Not recommended for occupied or customer-facing spaces.'
  };
  return levels[level] || levels['APPA 2'];
}

function defaultAreasForFacilityType(facilityType) {
  var defaults = {
    office: [
      { name: 'Open office', sqft: '', floorType: 'Carpet — commercial loop', notes: '' },
      { name: 'Private offices', sqft: '', floorType: 'Carpet — commercial loop', notes: '' },
      { name: 'Conference rooms', sqft: '', floorType: 'Carpet — commercial loop', notes: '' },
      { name: 'Restrooms', sqft: '', floorType: 'Ceramic / porcelain tile', notes: '' },
      { name: 'Break room', sqft: '', floorType: 'LVT / LVP (luxury vinyl)', notes: '' },
      { name: 'Reception / lobby', sqft: '', floorType: 'LVT / LVP (luxury vinyl)', notes: '' }
    ],
    medical: [
      { name: 'Patient rooms', sqft: '', floorType: 'LVT / LVP (luxury vinyl)', notes: 'Terminal cleaning required between occupants' },
      { name: 'Exam rooms', sqft: '', floorType: 'LVT / LVP (luxury vinyl)', notes: 'EPA-registered disinfectant required' },
      { name: 'Waiting area', sqft: '', floorType: 'Carpet — commercial loop', notes: '' },
      { name: 'Nurses\' station / admin', sqft: '', floorType: 'LVT / LVP (luxury vinyl)', notes: '' },
      { name: 'Restrooms', sqft: '', floorType: 'Ceramic / porcelain tile', notes: '' },
      { name: 'Break room / staff lounge', sqft: '', floorType: 'LVT / LVP (luxury vinyl)', notes: '' },
      { name: 'Corridors', sqft: '', floorType: 'LVT / LVP (luxury vinyl)', notes: '' }
    ],
    education: [
      { name: 'Classrooms', sqft: '', floorType: 'Carpet — commercial loop', notes: '' },
      { name: 'Hallways / corridors', sqft: '', floorType: 'VCT — vinyl composition tile', notes: '' },
      { name: 'Restrooms', sqft: '', floorType: 'Ceramic / porcelain tile', notes: '' },
      { name: 'Gymnasium', sqft: '', floorType: 'Hardwood — finished', notes: '' },
      { name: 'Cafeteria', sqft: '', floorType: 'Sealed concrete (cured >28 d)', notes: 'Food-contact surface disinfection required' },
      { name: 'Library / media center', sqft: '', floorType: 'Carpet — commercial loop', notes: '' },
      { name: 'Administrative offices', sqft: '', floorType: 'Carpet — commercial loop', notes: '' }
    ],
    retail: [
      { name: 'Sales floor', sqft: '', floorType: 'VCT — vinyl composition tile', notes: '' },
      { name: 'Fitting rooms', sqft: '', floorType: 'LVT / LVP (luxury vinyl)', notes: '' },
      { name: 'Restrooms', sqft: '', floorType: 'Ceramic / porcelain tile', notes: '' },
      { name: 'Break room / back of house', sqft: '', floorType: 'Sealed concrete (cured >28 d)', notes: '' },
      { name: 'Entrance / vestibule', sqft: '', floorType: 'Ceramic / porcelain tile', notes: 'High-traffic; mats required' }
    ],
    industrial: [
      { name: 'Production floor', sqft: '', floorType: 'Sealed concrete (cured >28 d)', notes: 'May require specialty degreaser; verify chemistry compatibility' },
      { name: 'Warehouse / storage', sqft: '', floorType: 'Sealed concrete (cured >28 d)', notes: '' },
      { name: 'Offices', sqft: '', floorType: 'Carpet — commercial loop', notes: '' },
      { name: 'Restrooms / locker rooms', sqft: '', floorType: 'Ceramic / porcelain tile', notes: '' },
      { name: 'Break room', sqft: '', floorType: 'LVT / LVP (luxury vinyl)', notes: '' }
    ],
    hospitality: [
      { name: 'Guest rooms', sqft: '', floorType: 'Carpet — commercial loop', notes: 'Daily turndown service; deep clean on checkout' },
      { name: 'Lobby / reception', sqft: '', floorType: 'Natural stone — marble / limestone / travertine', notes: 'pH-neutral cleaner only' },
      { name: 'Corridors', sqft: '', floorType: 'Carpet — commercial loop', notes: '' },
      { name: 'Restrooms / public', sqft: '', floorType: 'Ceramic / porcelain tile', notes: '' },
      { name: 'Restaurant / food service', sqft: '', floorType: 'Ceramic / porcelain tile', notes: 'Food-contact disinfection; grease removal' },
      { name: 'Fitness center', sqft: '', floorType: 'Rubber sport / mat flooring', notes: 'High-frequency disinfection; verify pH compatibility with rubber' },
      { name: 'Meeting / event rooms', sqft: '', floorType: 'Carpet — commercial loop', notes: '' }
    ],
    'multi-tenant': [
      { name: 'Common area lobby', sqft: '', floorType: 'Natural stone — granite', notes: '' },
      { name: 'Elevator cabs', sqft: '', floorType: 'Ceramic / porcelain tile', notes: 'Daily disinfection' },
      { name: 'Corridors / hallways', sqft: '', floorType: 'Carpet — commercial loop', notes: '' },
      { name: 'Restrooms (common)', sqft: '', floorType: 'Ceramic / porcelain tile', notes: '' },
      { name: 'Tenant suites (shared service)', sqft: '', floorType: 'Carpet — commercial loop', notes: 'Scope limited to common-area pass-through unless suite service added' },
      { name: 'Fitness / amenity floor', sqft: '', floorType: 'Rubber sport / mat flooring', notes: '' },
      { name: 'Loading dock / service area', sqft: '', floorType: 'Sealed concrete (cured >28 d)', notes: '' }
    ]
  };
  return defaults[facilityType] || defaults['office'];
}

function defaultFrequencyMatrix(facilityType) {
  var base = {
    daily: [
      'Empty waste receptacles and replace liners',
      'Dust/damp wipe horizontal surfaces and high-touch points',
      'Spot-clean glass and partition surfaces',
      'Vacuum or dust-mop hard floors',
      'Vacuum carpet (main traffic lanes)',
      'Clean and disinfect restroom fixtures, floors, mirrors, dispensers',
      'Restock restroom consumables (paper products, soap)',
      'Spot-clean entry glass and door hardware',
      'Remove visible debris from entrance matting'
    ],
    weekly: [
      'Damp-mop hard floors (full area)',
      'Vacuum carpet (full area, including edges)',
      'Wipe chairs, tables, and furniture bases',
      'Clean interior glass and window ledges',
      'Detail restroom partitions, grout, and baseboards',
      'High-dusting (vents, ledges, tops of partitions) on rotation'
    ],
    monthly: [
      'Detail high-dusting (ceiling vents, diffusers, light fixtures)',
      'Clean upholstered seating',
      'Scrub hard floor grout and baseboards',
      'Inspect and spot-treat carpet stains',
      'Wipe down interior window frames and sills'
    ],
    quarterly: [
      'Machine scrub hard floors',
      'Hot-water extract or encapsulation-clean carpet',
      'Clean interior of refrigerators and appliances (break room)',
      'Inspect floor finish condition; apply additional coats if needed',
      'Pest prevention walk-through support (report issues; no treatment)'
    ],
    annual: [
      'Strip and refinish hard floors (VCT — per manufacturer spec)',
      'Deep scrub hard floors (LVT / LVP) and apply manufacturer-approved protective seal — do not chemically strip like VCT',
      'Deep-clean carpet via hot-water extraction (full area)',
      'High-pressure or steam-clean tile grout (restrooms)',
      'Clean and inspect HVAC return grilles (surface-clean only)',
      'Update chemical inventory and SDS library in janitor closets'
    ]
  };

  if (facilityType === 'medical') {
    base.daily.push('Terminal clean patient/exam rooms after occupant discharge', 'EPA List N or List P disinfectant on all clinical contact surfaces');
    base.weekly.push('Disinfect medical equipment handles and cart rails (surface-clean; clinical staff responsibility for device internals)');
  }
  if (facilityType === 'education') {
    base.daily.push('Clean and disinfect cafeteria tables and food-prep surfaces');
    base.weekly.push('Wipe gymnasium bleachers and locker room benches');
  }
  if (facilityType === 'industrial') {
    base.daily.push('Sweep and degrease production floor (spot treatment)');
    base.weekly.push('Pressure-clean dock areas and equipment staging zones');
  }
  if (facilityType === 'hospitality') {
    base.daily.push('Full guest room turn (bed strip, linen exchange, surfaces, restroom)');
    base.weekly.push('Detail fitness equipment and pool deck surround');
  }
  return base;
}

function parseAreaSqft(v) {
  var n = Number(String(v == null ? '' : v).replace(/[^\d.]/g, ''));
  return isNaN(n) || n < 0 ? 0 : n;
}

function parseFixtures(v) {
  var n = Number(String(v == null ? '' : v).replace(/[^\d]/g, ''));
  return isNaN(n) || n < 0 ? 0 : Math.round(n);
}

function isRestroomArea(area) {
  return /restroom|locker|toilet|washroom/.test(String(area && area.name || '').toLowerCase());
}

function isStoneFloorType(ft) {
  return /stone|marble|granite|travertine|limestone/.test(String(ft || '').toLowerCase());
}

function stoneAreaNames(areas) {
  var names = [];
  (areas || []).forEach(function (a) {
    if (!isStoneFloorType(a && a.floorType)) return;
    names.push((a && a.name) ? String(a.name) : 'natural stone surfaces');
  });
  return names;
}

function restroomsMissingFixtures(areas) {
  var missing = [];
  (areas || []).forEach(function (a) {
    if (!isRestroomArea(a)) return;
    if (parseAreaSqft(a && a.sqft) <= 0) return;
    if (parseFixtures(a && a.fixtures) > 0) return;
    missing.push((a && a.name) ? String(a.name) : 'Restroom');
  });
  return missing;
}

function sumAreaSqft(areas) {
  var sum = 0;
  (areas || []).forEach(function (a) { sum += parseAreaSqft(a && a.sqft); });
  return sum;
}

/** Compare facility total vs sum of area line items. Flag when areas have sqft and diverge >5% or >50 sf. */
function sqftBalance(state) {
  var facility = parseAreaSqft(state && state.totalSqft);
  var areasSum = sumAreaSqft(state && state.areas);
  var areasWithSqft = 0;
  (state && state.areas || []).forEach(function (a) {
    if (parseAreaSqft(a && a.sqft) > 0) areasWithSqft += 1;
  });
  var delta = areasSum - facility;
  var tolerance = Math.max(50, facility * 0.05);
  var mismatch = areasWithSqft > 0 && facility > 0 && Math.abs(delta) > tolerance;
  return {
    facility: facility,
    areasSum: areasSum,
    delta: delta,
    areasWithSqft: areasWithSqft,
    mismatch: mismatch,
    tolerance: tolerance
  };
}

function autoScaleAreasToFacility(areas, facilityTotal) {
  var list = areas || [];
  var target = parseAreaSqft(facilityTotal);
  if (!list.length || target <= 0) return list;
  var originals = list.map(function (a) { return parseAreaSqft(a.sqft); });
  var sum = originals.reduce(function (s, n) { return s + n; }, 0);
  if (sum <= 0) {
    var each = Math.floor(target / list.length);
    var rem = target - each * list.length;
    list.forEach(function (a, i) {
      a.sqft = String(each + (i === list.length - 1 ? rem : 0));
    });
    return list;
  }
  var scaled = originals.map(function (n) {
    if (n <= 0) return 0;
    // Keep at least 1 sf for any area that had a value (when facility can support it)
    return Math.max(1, Math.round(target * (n / sum)));
  });
  var allocated = scaled.reduce(function (s, n) { return s + n; }, 0);
  var drift = allocated - target;
  // Trim or grow the largest line until totals match
  while (drift !== 0 && scaled.length) {
    var idx = 0;
    for (var i = 1; i < scaled.length; i++) {
      if (scaled[i] > scaled[idx]) idx = i;
    }
    if (drift > 0) {
      if (scaled[idx] <= 1) break;
      scaled[idx] -= 1;
      drift -= 1;
    } else {
      scaled[idx] += 1;
      drift += 1;
    }
  }
  list.forEach(function (a, i) { a.sqft = String(scaled[i] || 0); });
  return list;
}

/** Surface flags from Step 2 floor types (and area names for restrooms). */
function detectSurfaces(areas) {
  var s = { vct: false, lvt: false, carpet: false, tile: false, concrete: false, epoxy: false, hardwood: false, rubber: false, stone: false, restroom: false };
  (areas || []).forEach(function (a) {
    var ft = String(a && a.floorType || '').toLowerCase();
    var name = String(a && a.name || '').toLowerCase();
    if (/vct/.test(ft)) s.vct = true;
    if (/\blvt\b|\blvp\b|luxury vinyl/.test(ft)) s.lvt = true;
    if (/carpet/.test(ft)) s.carpet = true;
    if (/ceramic|porcelain/.test(ft) || (/tile/.test(ft) && !/vct|lvt|lvp|vinyl composition/.test(ft))) s.tile = true;
    if (/sealed concrete|concrete/.test(ft)) s.concrete = true;
    if (/epoxy|urethane/.test(ft)) s.epoxy = true;
    if (/hardwood/.test(ft)) s.hardwood = true;
    if (/rubber/.test(ft)) s.rubber = true;
    if (/stone|marble|granite|travertine|limestone/.test(ft)) s.stone = true;
    if (/restroom|locker|toilet|washroom/.test(name)) s.restroom = true;
  });
  return s;
}

/**
 * Drop or rewrite frequency tasks that contradict selected floor surfaces.
 * e.g. no VCT → remove strip/refinish; LVT gets deep scrub + protective seal (never chemical strip).
 */
function filterTasksForSurfaces(freq, areas) {
  var floors = detectSurfaces(areas);
  var out = {};
  var keys = ['daily', 'weekly', 'monthly', 'quarterly', 'annual'];
  keys.forEach(function (k) {
    out[k] = (freq && freq[k] ? freq[k].slice() : []);
  });

  function keep(task) {
    var t = String(task || '');
    var tl = t.toLowerCase();
    if (!t.trim()) return true;

    // Strip/refinish is VCT-only — never for LVT/LVP
    if (/strip and refinish/.test(tl)) {
      if (!floors.vct) return false;
      return true;
    }
    // LVT deep scrub / protective seal
    if (/deep scrub/.test(tl) && /lvt|lvp|luxury vinyl/.test(tl)) {
      return !!floors.lvt;
    }
    // Carpet-specific tasks
    if (/\bcarpet\b/.test(tl) && !floors.carpet) return false;
    // Tile grout (ceramic/porcelain), not VCT
    if (/tile grout|grout \(restrooms\)|steam-clean tile/.test(tl) && !floors.tile && !floors.restroom) return false;
    return true;
  }

  var LVT_ANNUAL = 'Deep scrub hard floors (LVT / LVP) and apply manufacturer-approved protective seal — do not chemically strip like VCT';
  var VCT_ANNUAL = 'Strip and refinish hard floors (VCT — per manufacturer spec)';

  function expand(task) {
    var t = String(task || '');
    var tl = t.toLowerCase();
    // Legacy combined strip line → surface-correct task(s)
    if (/strip and refinish/.test(tl) && /vct|lvt|lvp/.test(tl)) {
      var expanded = [];
      if (floors.vct) expanded.push(VCT_ANNUAL);
      if (floors.lvt) expanded.push(LVT_ANNUAL);
      return expanded.length ? expanded : [];
    }
    if (/strip and refinish/.test(tl) && floors.vct) return [VCT_ANNUAL];
    if (/deep scrub/.test(tl) && /lvt|lvp/.test(tl) && floors.lvt) return [LVT_ANNUAL];
    return [t];
  }

  keys.forEach(function (k) {
    var next = [];
    var seen = {};
    (out[k] || []).forEach(function (task) {
      expand(task).filter(keep).forEach(function (line) {
        if (seen[line]) return;
        seen[line] = true;
        next.push(line);
      });
    });
    out[k] = next;
  });
  return out;
}

/** Map area floor types → Production Rate mix % for labor estimate. */
function mixPctFromAreas(areas, facilityTotal) {
  var buckets = { carpet: 0, hard_floor: 0, restroom: 0, office: 0, common: 0 };
  var sum = 0;
  (areas || []).forEach(function (a) {
    var sf = parseAreaSqft(a && a.sqft);
    if (sf <= 0) return;
    var ft = String(a.floorType || '').toLowerCase();
    var name = String(a.name || '').toLowerCase();
    var key = 'common';
    if (/restroom|locker|toilet|washroom/.test(name)) key = 'restroom';
    else if (/carpet/.test(ft)) key = 'carpet';
    else if (/office|conference|admin|classroom|guest room|suite/.test(name)) key = 'office';
    else if (/vct|lvt|lvp|vinyl|ceramic|porcelain|tile|concrete|epoxy|hardwood|stone|marble|granite|rubber/.test(ft)) key = 'hard_floor';
    buckets[key] += sf;
    sum += sf;
  });
  var total = sum > 0 ? sum : (Number(facilityTotal) || 0);
  if (total <= 0 || sum <= 0) {
    return { pct_carpet: 40, pct_hard_floor: 20, pct_restroom: 5, pct_office: 25, pct_common: 10 };
  }
  return {
    pct_carpet: (buckets.carpet / sum) * 100,
    pct_hard_floor: (buckets.hard_floor / sum) * 100,
    pct_restroom: (buckets.restroom / sum) * 100,
    pct_office: (buckets.office / sum) * 100,
    pct_common: (buckets.common / sum) * 100
  };
}

function estimateLaborFromProduction(state) {
  var shared = null;
  if (typeof window !== 'undefined' && window.OporaCalcShared) shared = window.OporaCalcShared;
  else if (typeof globalThis !== 'undefined' && globalThis.OporaCalcShared) shared = globalThis.OporaCalcShared;
  if (!shared || typeof shared.productionCalc !== 'function') return null;

  var facility = Number(state.totalSqft) || sumAreaSqft(state.areas);
  if (!facility || facility <= 0) return null;
  var appa = String(state.qualityLevel || 'APPA 2').replace(/\D/g, '') || '2';
  var mix = mixPctFromAreas(state.areas, facility);
  var r = shared.productionCalc({
    total_sqft: facility,
    appa_level: appa,
    workdays: 5,
    pct_carpet: mix.pct_carpet,
    pct_hard_floor: mix.pct_hard_floor,
    pct_restroom: mix.pct_restroom,
    pct_office: mix.pct_office,
    pct_common: mix.pct_common,
    shift_timing: 'nightly',
    soil_density: 'medium'
  });
  if (!r || !r.valid) return null;
  return {
    nightly_hours: r.nightly_hours,
    weekly_hours: r.weekly_hours,
    annual_hours: r.annual_hours,
    fte: r.fte,
    blended_sf_per_hr: r.blended_sf_per_hr,
    appa: appa
  };
}

function validateStep(stepIndex, state) {
  var errors = [];
  var step = Number(stepIndex);
  if (step === 1) {
    if (!state.facilityType) errors.push('Facility type is required.');
    // Prefer the live input so we never flag "required" while the box still shows a value
    // (stale state, prefill races, or comma-formatted numbers).
    var sqftRaw = state && state.totalSqft;
    try {
      var sqftEl = typeof document !== 'undefined' ? document.getElementById('sow-total-sqft') : null;
      if (sqftEl && sqftEl.value != null) sqftRaw = sqftEl.value;
    } catch (e) { /* ignore */ }
    var sqftStr = String(sqftRaw == null ? '' : sqftRaw).trim();
    var sqftNum = parseAreaSqft(sqftStr);
    // Empty box only — placeholders do not count as a value
    if (!sqftStr) {
      errors.push('Enter total square footage.');
    } else if (!(sqftNum > 0)) {
      errors.push('Total square footage must be a number greater than zero.');
    } else if (state) {
      state.totalSqft = String(Math.round(sqftNum));
    }
    if (!state.numFloors || Number(state.numFloors) <= 0) errors.push('Number of floors must be at least 1.');
  }
  if (step === 2) {
    if (!state.areas || state.areas.length === 0) errors.push('At least one area is required.');
    for (var i = 0; i < (state.areas || []).length; i++) {
      var a = state.areas[i];
      if (!a.name || !a.name.trim()) errors.push('Area #' + (i + 1) + ' must have a name.');
    }
    // Sq ft balance mismatch is a soft warning in the Areas UI — do not hard-block Continue.
  }
  return { valid: errors.length === 0, errors: errors };
}

function serializeState(state) {
  // Plain JSON — URLSearchParams encodes once. Do not pre-encode (that made %257B… links).
  try { return JSON.stringify(state); } catch (e) { return ''; }
}

function deserializeState(str) {
  try {
    if (!str) return null;
    try { return JSON.parse(str); } catch (e1) {
      // Legacy share links were double-encoded via encodeURIComponent + searchParams
      return JSON.parse(decodeURIComponent(str));
    }
  } catch (e) { return null; }
}

var EXHIBITS_NOTE = 'Append supporting exhibits before execution (area map, SDS / COI, pricing schedule).';

/** Strip leftover developer brackets so PDFs never look unfinished. */
function scrubBracketPlaceholders(text) {
  return String(text == null ? '' : text)
    .replace(/\[Facility Name(?:\s*\/\s*Property Owner)?\]/gi, 'Facility Owner/Manager')
    .replace(/\[sqft TBD\]/gi, 'sq ft to be confirmed')
    .replace(/\[TBD\]/gi, 'to be confirmed')
    .replace(/\[To be specified[^\]]*\]/gi, 'to be confirmed')
    .replace(/\[placeholder[^\]]*\]/gi, '')
    .replace(/\[Note:[^\]]*\]/gi, '')
    .replace(/\[No tasks specified[^\]]*\]/gi, 'No tasks specified for this frequency.')
    .replace(/\[X\]%/gi, '3%')
    .replace(/\[[^\]]{1,120}\]/g, function (m) {
      /* Keep intentional signature blanks like _________________ outside brackets */
      if (/^\[\s*_+\s*\]$/.test(m)) return m;
      return 'to be confirmed';
    })
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ +\./g, '.')
    .replace(/\n{3,}/g, '\n\n');
}

function buildScopeDocument(state) {
  var facilityNameRaw = (state.facilityName && state.facilityName.trim()) ? state.facilityName.trim() : '';
  var facilityName = facilityNameRaw || 'the named facility';
  var facilityType = state.facilityType || 'office';
  var totalSqft = Number(state.totalSqft) || 0;
  var numFloors = Number(state.numFloors) || 1;
  var hoursOfOperation = (state.hoursOfOperation && String(state.hoursOfOperation).trim())
    ? String(state.hoursOfOperation).trim()
    : 'To be confirmed with Facility Manager';
  var accessRequirements = state.accessRequirements || 'Standard key / access card. Contractor to coordinate with facilities manager for after-hours access.';
  var areas = state.areas || defaultAreasForFacilityType(facilityType);
  var freqRaw = state.frequencyMatrix || defaultFrequencyMatrix(facilityType);
  var freq = filterTasksForSurfaces(freqRaw, areas);
  var qualityLevel = state.qualityLevel || defaultQualityLevel();
  var certifications = state.certifications || '';
  var insuranceGL = state.insuranceGL || '$1,000,000';
  var insuranceAuto = state.insuranceAuto || '$1,000,000';
  var insuranceWC = state.insuranceWC || '$500,000';
  var insuranceUmbrella = state.insuranceUmbrella || '1000000';
  var additionalInsured = (state.additionalInsured && String(state.additionalInsured).trim())
    ? String(state.additionalInsured).trim()
    : (facilityNameRaw || 'Facility Owner/Manager');
  var contractTerm = (state.contractTerm && String(state.contractTerm).trim())
    ? String(state.contractTerm).trim()
    : '1 year with renewal options';
  var escalationCap = state.escalationCap || '3';
  var exclusions = state.exclusions || [
    'Supply of restroom consumables (paper products, soap, liners) unless separately quoted',
    'Periodic floor restoration (strip-and-wax, carpet extraction) beyond quarterly schedule unless included in addendum',
    'Biohazard, sharps, or regulated medical waste handling',
    'Post-construction or post-renovation cleaning',
    'Exterior window cleaning above first floor',
    'Pest control treatment',
    'Laundry or linen services',
    'Moving or rearranging furniture heavier than 30 lbs'
  ];
  if (typeof exclusions === 'string') {
    exclusions = exclusions.split('\n').map(function (l) { return l.trim(); }).filter(Boolean);
  }

  var sqftPhrase = totalSqft ? totalSqft.toLocaleString() : 'to be confirmed';
  var escalationPhrase = escalationCap === 'cpi-only'
    ? 'changes in the Consumer Price Index (CPI-U) for the applicable metropolitan statistical area'
    : 'changes in the Consumer Price Index (CPI-U) for the applicable metropolitan statistical area, or ' + escalationCap + '%, whichever is lower';

  var umbrellaLine = '';
  if (insuranceUmbrella === 'none') {
    umbrellaLine = '';
  } else if (insuranceUmbrella === 'tbd') {
    umbrellaLine = '  \u2022 Umbrella / Excess Liability: To be determined based on final RFP risk evaluation\n';
  } else {
    var umbrellaAmtMap = {
      '1000000': '$1,000,000',
      '2000000': '$2,000,000',
      '5000000': '$5,000,000'
    };
    var umbrellaAmt = umbrellaAmtMap[String(insuranceUmbrella)] || String(insuranceUmbrella);
    if (umbrellaAmt.charAt(0) !== '$' && /^\d/.test(umbrellaAmt)) {
      umbrellaAmt = '$' + Number(String(umbrellaAmt).replace(/[^\d]/g, '')).toLocaleString();
    }
    umbrellaLine = '  \u2022 Umbrella / Excess Liability: ' + umbrellaAmt + ' per occurrence\n';
  }

  var todayStr = new Date().toISOString().slice(0, 10);
  var areasSum = sumAreaSqft(areas);
  var bal = sqftBalance({ totalSqft: totalSqft, areas: areas });

  var areasPlainLines = areas.map(function (a) {
    var sqftNum = parseAreaSqft(a.sqft);
    var sqft = sqftNum ? sqftNum.toLocaleString() + ' sq ft' : 'sq ft to be confirmed';
    var line = '  \u2022 ' + (a.name || '\u2014') + ' \u2014 ' + sqft + ' \u2014 ' + (a.floorType || 'TBD');
    var fx = parseFixtures(a && a.fixtures);
    if (isRestroomArea(a) && fx > 0) line += ' \u2014 ' + fx + ' fixture' + (fx === 1 ? '' : 's');
    if (a.notes) line += ' (' + a.notes + ')';
    return line;
  }).join('\n');

  var areasReconcile = '\n\nTotal facility: approximately ' + sqftPhrase +
    ' sq ft across ' + numFloors + ' floor' + (numFloors !== 1 ? 's' : '') + '.';
  if (areasSum > 0) {
    areasReconcile += '\nSum of listed areas: ' + areasSum.toLocaleString() + ' sq ft.';
    if (bal.mismatch) {
      areasReconcile += '\nWARNING: Area line items do not reconcile to the facility total — verify sq ft before bidding. Labor staffing below uses the facility total unless you update it.';
    }
  }
  var missingFixtures = restroomsMissingFixtures(areas);
  if (missingFixtures.length) {
    areasReconcile += '\nNOTE: Restroom fixture counts are unpopulated for ' + missingFixtures.join(', ') +
      '. Fixture count drives restroom labor time — add counts before locking staffing.';
  }

  var stoneNames = stoneAreaNames(areas);
  var stoneChemicalClause = '';
  if (stoneNames.length) {
    var stoneList = stoneNames.join('; ');
    stoneChemicalClause = '\n  \u2022 Contractor shall use strictly pH-neutral stone cleaner (pH 7.0) only on ' +
      stoneList +
      '. Acidic or alkaline multi-surface cleaners are prohibited on these surfaces.';
  }

  function freqBlock(tasks) {
    if (!tasks || tasks.length === 0) return '  \u2022 No tasks specified for this frequency.';
    return tasks.map(function (t) { return '  \u2022 ' + t; }).join('\n');
  }

  var labor = estimateLaborFromProduction(state);
  var laborBlock = '';
  if (labor) {
    laborBlock = '\n\nEstimated cleaning labor (Opora Production Rate model — APPA ' + labor.appa +
      ', Nightly 5×/week baseline; surface mix inferred from area floor types):\n' +
      '  \u2022 Hours per visit: **' + labor.nightly_hours.toFixed(2) + ' hr**\n' +
      '  \u2022 Weekly hours: **' + labor.weekly_hours.toFixed(1) + ' hr**\n' +
      '  \u2022 Annual hours: **' + Math.round(labor.annual_hours).toLocaleString() + ' hr** (~**' +
      labor.fte.toFixed(2) + ' FTE**)\n' +
      '  \u2022 Blended production: **' + Math.round(labor.blended_sf_per_hr).toLocaleString() + ' sqft/hr**\n' +
      'These hours are planning estimates for staffing and bid build-up. Walk the building to validate before locking price.';
  } else {
    laborBlock = '\n\nEstimated cleaning labor: Open the Production Rate Calculator with this facility sq ft to attach hours/visit to the bid. Walk the building before locking price.';
  }
  var documentStatus = (facilityNameRaw && totalSqft > 0)
    ? 'CONTRACT READY'
    : 'EXHIBIT — DRAFT';

  var appaDesc = appaDescription(qualityLevel);
  var certList = certifications
    ? certifications.split(',').map(function (c) { return c.trim(); }).filter(Boolean).map(function (c) { return '  \u2022 ' + c; }).join('\n')
    : '  \u2022 None specified at time of document generation. Contractor should disclose all current certifications in bid response.';
  var exclusionsList = exclusions.length
    ? exclusions.map(function (e) { return '  \u2022 ' + e; }).join('\n')
    : '  \u2022 None listed.';

  var signatureContent =
    'By signing below, the authorized representatives of both parties agree that this Scope of Work accurately reflects the services to be provided and the standards to be maintained.\n\n' +
    'Facility Owner / Manager\n\n' +
    '  Name: _______________________________\n' +
    '  Title: _______________________________\n' +
    '  Organization: ' + (facilityNameRaw || additionalInsured) + '\n' +
    '  Date: _______________________________\n' +
    '  Signature: __________________________\n\n\n' +
    'Contractor\n\n' +
    '  Company Name: _______________________\n' +
    '  Name: _______________________________\n' +
    '  Title: _______________________________\n' +
    '  Date: _______________________________\n' +
    '  Signature: __________________________\n\n\n' +
    EXHIBITS_NOTE;

  var insuranceSection =
    'Contractor must maintain, at minimum, the following insurance coverage throughout the term of any agreement arising from this SOW. Minimums below are for general commercial use and must be reviewed with your insurance broker and legal counsel before final RFP issuance:\n\n' +
    '  \u2022 Commercial General Liability: ' + insuranceGL + ' per occurrence / $2,000,000 aggregate\n' +
    '  \u2022 Commercial Auto Liability: ' + insuranceAuto + ' combined single limit\n' +
    '  \u2022 Workers\' Compensation: Statutory limits per applicable state law\n' +
    '  \u2022 Employers\' Liability: ' + insuranceWC + '\n' +
    umbrellaLine +
    '\nCertificates of insurance naming ' + additionalInsured + ' as additional insured must be provided prior to contract execution and renewed annually.\n\n' +
    'Certifications required or preferred:\n' + certList;

  var termSection =
    'Initial term: ' + contractTerm + '.\n\n' +
    'Pricing structure: Contractor to propose a firm monthly fixed fee covering all recurring services in this SOW. Any additional or periodic services (quarterly, annual) may be quoted as unit-price line items. A cost-in-use breakdown (labor hours, chemical cost, equipment amortization) is requested as a supporting exhibit to the bid.\n\n' +
    'Escalation: Annual price adjustments, if any, shall be limited to ' + escalationPhrase + ', and require 60 days\' written notice prior to the renewal anniversary date.\n\n' +
    'Termination: Either party may terminate this agreement for cause with 30 days\' written notice following a cure period of 10 business days. Owner may terminate for convenience with 60 days\' written notice.\n\n' +
    'Pricing and term provisions in this section are subject to final review by the facility\'s legal and procurement teams before use in a binding agreement.';

  var sections = [
    { title: '1. Introduction and Facility Overview', content: 'This Scope of Work (SOW) defines the commercial cleaning and custodial services required for ' + facilityName + ', a ' + facilityType + ' facility comprising approximately ' + sqftPhrase + ' square feet across ' + numFloors + ' floor' + (numFloors !== 1 ? 's' : '') + '.\n\nThis document is intended for use in a Request for Proposal (RFP) process or as the service specification exhibit attached to a custodial services agreement. All service frequencies, quality standards, and personnel requirements described herein are minimum standards. The awarded contractor may exceed these standards at no additional cost to the facility owner.\n\nTerms used: "Contractor" refers to the cleaning service provider. "Owner" or "Facility Manager" refers to ' + facilityName + ' or its designated representative. "Service Area" refers to any area identified in Section 3 of this document.' },
    { title: '2. Hours of Operation and Access', content: 'Facility hours of operation: ' + hoursOfOperation + '\n\nCleaning service hours: To be proposed by Contractor. Standard preference is service outside of peak occupancy hours. Any deviation from the agreed service window requires 24-hour advance notice to the Facility Manager.\n\nAccess requirements:\n' + accessRequirements + '\n\nThe Contractor is responsible for maintaining the security of all keys, access cards, and entry codes provided. Lost credentials must be reported immediately and replaced at the Contractor\'s expense. Contractor personnel may not share credentials or leave any entrance unsecured.' + laborBlock },
    { title: '3. Areas Covered', content: 'The following areas are included in the scope of recurring cleaning services. Square footages are approximate and may be verified by Contractor during a pre-bid walk-through.\n\n' + areasPlainLines + areasReconcile + '\n\nAreas not listed above are excluded from recurring service unless added by written addendum signed by both parties.' },
    { title: '4. Daily Tasks', content: 'The following tasks are to be performed on every scheduled service visit (typically each business day unless otherwise agreed):\n\n' + freqBlock(freq.daily) + '\n\nAll daily tasks apply to all service areas unless a specific area is noted as an exception in Section 3. Restroom tasks apply each service visit regardless of occupancy level.' },
    { title: '5. Weekly Tasks', content: 'The following tasks are to be performed once per calendar week on a consistent schedule agreed with the Facility Manager:\n\n' + freqBlock(freq.weekly) + '\n\nThe Contractor must maintain a written or digital task log indicating the date and assigned personnel for each weekly task. Logs are subject to audit per Section 8.' },
    { title: '6. Monthly Tasks', content: 'The following tasks are to be performed once per calendar month. Scheduling must be communicated to the Facility Manager at least five business days in advance:\n\n' + freqBlock(freq.monthly) + '\n\nMonthly tasks may require temporary access to areas outside normal service hours. Contractor to coordinate with Facility Manager.' },
    { title: '7. Quarterly and Annual Tasks', content: 'Quarterly tasks (every three months):\n\n' + freqBlock(freq.quarterly) + '\n\nAnnual tasks (once per calendar year, typically during reduced-occupancy period):\n\n' + freqBlock(freq.annual) + '\n\nQuarterly and annual tasks require a minimum of ten business days\' advance notice and written sign-off from the Facility Manager. Any additional cost beyond the base contract must be disclosed in the original bid as a line-item unit price.' },
    { title: '8. Quality Standards and Inspection', content: 'Required quality level: ' + qualityLevel + '\n\n' + appaDesc + '\n\nInspection cadence: Contractor shall conduct self-inspections using a standardized checklist at minimum once per week and provide results to the Facility Manager monthly. The Facility Manager or a designated third party may conduct independent inspections at any time without prior notice.\n\nDeficiency correction: Any deficiency identified during inspection must be corrected within 24 hours of notification unless the deficiency requires scheduling coordination, in which case a written correction plan is due within 48 hours.\n\nInspection forms: Contractor shall use a documented inspection format. Acceptable formats include ISSA CIMS-based inspection protocols, APPA Appearance Standards worksheets, or a Contractor-proprietary format approved in writing by the Facility Manager prior to contract execution.' },
    { title: '9. Equipment and Supplies', content: 'Contractor furnishes all cleaning equipment, tools, chemicals, and supplies necessary to perform the services described in this SOW unless explicitly stated otherwise below.\n\nChemical standards:\n  \u2022 All cleaning chemistries must have current Safety Data Sheets (SDS) on file in the janitor closet at all times.\n  \u2022 Disinfectants used in this facility must be EPA-registered for the intended application.\n  \u2022 No product substitutions are permitted without written approval from the Facility Manager.\n  \u2022 Contractor shall use color-coded microfiber or dedicated tools to prevent cross-contamination between restroom and non-restroom areas.' + stoneChemicalClause + '\n\nEquipment standards:\n  \u2022 All equipment must be in safe operating condition. Evidence of unrepaired damage is grounds for immediate removal from service.\n  \u2022 Battery-operated or low-emission equipment preferred for enclosed areas.\n  \u2022 No equipment may be stored on-site without written authorization.\n\nConsumables: Unless separately quoted as an addendum, restroom consumables (paper towels, toilet tissue, hand soap, liners, sanitary products) are excluded from this scope and remain the responsibility of the Owner or a separate consumables supplier.' },
    { title: '10. Personnel Requirements', content: 'All personnel performing services under this agreement must meet the following minimum requirements:\n\n  \u2022 Background check: Criminal background screen (minimum 7-year lookback) completed prior to first access to the facility. Contractor maintains records and provides verification upon request.\n  \u2022 Training: All personnel must complete documented new-hire orientation covering chemical safety (GHS/HazCom), bloodborne pathogen awareness (29 CFR 1910.1030 for applicable environments), and facility-specific access and security protocols.\n  \u2022 Ongoing training: Minimum annual refresher training on chemical handling, SDS interpretation, and quality standards.\n  \u2022 Supervision: A designated lead or supervisor must be reachable by phone during all service hours. Supervisor must conduct on-site check-ins at a frequency agreed in the service agreement.\n  \u2022 Identification: All personnel must wear a uniform or identification badge clearly displaying company name and employee name while on-site.\n  \u2022 Subcontractors: Any use of subcontractors requires advance written notice to and written approval from the Facility Manager. All subcontractor personnel are subject to the same background, training, and identification requirements.' },
    { title: '11. Insurance and Certifications Required', content: insuranceSection },
    { title: '12. Exclusions', content: 'The following services are explicitly excluded from this Scope of Work. Any excluded service may be added by written change order at a negotiated unit price:\n\n' + exclusionsList },
    { title: '13. Communication and Reporting Requirements', content: 'Day-to-day communication: Contractor shall designate a primary contact (name, phone, email) for all service-related communications. Response time expectation: within 2 business hours during service hours; within 4 hours for off-hours urgent matters.\n\nMonthly report: Contractor shall submit a brief written service report each month including:\n  \u2022 Summary of all tasks completed vs. scope\n  \u2022 Inspection results and any deficiency log\n  \u2022 Incidents, near-misses, or damage reports\n  \u2022 Personnel changes (new hires, terminations affecting this account)\n  \u2022 Chemical inventory summary\n\nIncident reporting: Any property damage, personal injury, spill, or security incident on-site must be reported to the Facility Manager within 1 hour of occurrence. A written incident report is due within 24 hours.\n\nService log: A physical or digital log must be maintained on-site or accessible via a shared platform showing date, time in/out, and personnel for each service visit. This log is the property of the facility and must be provided upon request.' },
    { title: '14. Term, Pricing Structure, and Escalation', content: termSection },
    { title: '15. Signature Block', content: signatureContent }
  ];

  /* Golden rule: never ship literal developer brackets in generated copy */
  sections.forEach(function (s) {
    s.content = scrubBracketPlaceholders(s.content);
  });

  return {
    facilityName: facilityNameRaw || facilityName,
    facilityType: facilityType,
    totalSqft: totalSqft,
    numFloors: numFloors,
    areas: areas,
    additionalInsured: additionalInsured,
    generatedDate: todayStr,
    documentStatus: documentStatus,
    labor: labor || null,
    sections: sections
  };
}

function docToPlainText(doc) {
  var lines = [];
  lines.push('SCOPE OF WORK');
  lines.push(doc.facilityName);
  lines.push('Status: ' + (doc.documentStatus || 'EXHIBIT — DRAFT'));
  lines.push('Date: ' + doc.generatedDate);
  lines.push('');
  doc.sections.forEach(function (s, idx) {
    lines.push('');
    lines.push(s.title.toUpperCase());
    lines.push('-'.repeat(Math.min(s.title.length, 60)));
    var body = String(s.content || '').replace(/\*\*/g, '');
    /* Section 2: strip embedded labor prose; append structured metrics */
    if (idx === 1) {
      body = body.split(/\n\nEstimated cleaning labor/)[0] || body;
      lines.push(body);
      var labor = doc.labor;
      lines.push('');
      if (labor) {
        lines.push('Estimated cleaning labor (APPA ' + labor.appa + ', Nightly 5×/week):');
        lines.push('  Hours / visit: ' + labor.nightly_hours.toFixed(2) + ' hr');
        lines.push('  Weekly: ' + labor.weekly_hours.toFixed(1) + ' hr');
        lines.push('  Annual: ' + Math.round(labor.annual_hours).toLocaleString() + ' hr');
        lines.push('  FTE: ~' + labor.fte.toFixed(2));
        lines.push('  Blended: ' + Math.round(labor.blended_sf_per_hr).toLocaleString() + ' sf/hr');
        lines.push('Planning estimates — walk the building before locking price.');
      } else {
        lines.push('Estimated cleaning labor: Open the Production Rate Calculator to attach hours before locking price.');
      }
      return;
    }
    lines.push(body);
  });
  /* No platform footer / branding in clipboard — clean paste into Word/Docs */
  return lines.join('\n').replace(/\n?Generated by Opora[^\n]*/gi, '').trim() + '\n';
}

if (typeof window !== 'undefined') {
(function (global) {
  'use strict';

  var STORAGE_KEY = 'opora_scope_draft';
  var MAX_STEP = 5;

  var _state = {
    step: 1,
    facilityName: '',
    facilityType: 'office',
    totalSqft: '',
    numFloors: '1',
    hoursOfOperation: 'Monday–Friday, 8:00 AM – 6:00 PM',
    accessRequirements: '',
    areas: null,
    frequencyMatrix: null,
    qualityLevel: 'APPA 2',
    certifications: '',
    insuranceGL: '$1,000,000',
    insuranceAuto: '$1,000,000',
    insuranceWC: '$500,000',
    insuranceUmbrella: '1000000',
    additionalInsured: '',
    contractTerm: '1 year with renewal options',
    escalationCap: '3',
    exclusionsList: '',
    exhibits: null
  };

  var SG = {
    slugify: slugify,
    sqftBucket: sqftBucket,
    defaultQualityLevel: defaultQualityLevel,
    appaDescription: appaDescription,
    defaultAreasForFacilityType: defaultAreasForFacilityType,
    defaultFrequencyMatrix: defaultFrequencyMatrix,
    parseAreaSqft: parseAreaSqft,
    parseFixtures: parseFixtures,
    isRestroomArea: isRestroomArea,
    isStoneFloorType: isStoneFloorType,
    stoneAreaNames: stoneAreaNames,
    restroomsMissingFixtures: restroomsMissingFixtures,
    sumAreaSqft: sumAreaSqft,
    sqftBalance: sqftBalance,
    autoScaleAreasToFacility: autoScaleAreasToFacility,
    detectSurfaces: detectSurfaces,
    filterTasksForSurfaces: filterTasksForSurfaces,
    estimateLaborFromProduction: estimateLaborFromProduction,
    validateStep: validateStep,
    serializeState: serializeState,
    deserializeState: deserializeState,
    buildScopeDocument: buildScopeDocument,
    scrubBracketPlaceholders: scrubBracketPlaceholders,
    docToPlainText: docToPlainText
  };

  function _steps() { return document.querySelectorAll('.sow-step'); }
  function _stepBtn(n) { return document.querySelector('[data-step-btn="' + n + '"]'); }

  function _showStep(n) {
    _state.step = n;
    var panels = _steps();
    panels.forEach(function (p) {
      var s = parseInt(p.getAttribute('data-step'), 10);
      p.style.display = (s === n) ? '' : 'none';
    });
    for (var i = 1; i <= MAX_STEP; i++) {
      var btn = _stepBtn(i);
      if (!btn) continue;
      btn.classList.remove('sow-stepper__btn--active', 'sow-stepper__btn--done');
      if (i === n) btn.classList.add('sow-stepper__btn--active');
      else if (i < n) btn.classList.add('sow-stepper__btn--done');
    }
    var mobileLabel = document.getElementById('sow-mobile-step-label');
    if (mobileLabel) mobileLabel.textContent = 'Step ' + n + ' of ' + MAX_STEP;
    var root = document.getElementById('sow-wizard-root');
    if (root) root.classList.toggle('sow-wizard--previewing', n === 5);
    try {
      document.documentElement.classList.toggle('sow-wizard-previewing', n === 5);
    } catch (ePrev) {}
    var dock = document.getElementById('sow-float-dock');
    if (dock && n !== 5) dock.hidden = true;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    _saveState();
    if (n === 5) _renderPreview();
  }

  function _saveState() {
    // Draft stays in localStorage only — do not rewrite the address bar on every keystroke.
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_state)); } catch (e) {}
  }

  function _cleanSowQueryFromUrl() {
    try {
      var url = new URL(window.location.href);
      if (!url.searchParams.has('sow')) return;
      url.searchParams.delete('sow');
      var qs = url.searchParams.toString();
      window.history.replaceState(null, '', url.pathname + (qs ? '?' + qs : '') + url.hash);
    } catch (e) {}
  }

  function _loadState() {
    try {
      var url = new URL(window.location.href);
      var param = url.searchParams.get('sow');
      if (param) {
        var parsed = deserializeState(param);
        if (parsed && parsed.ft) {
          _state.facilityName = parsed.fn || '';
          _state.facilityType = parsed.ft || 'office';
          _state.totalSqft = parsed.sqft || '';
          _state.numFloors = parsed.fl || '1';
          _state.qualityLevel = parsed.ql || 'APPA 2';
          if (parsed.step) _state.step = Number(parsed.step) || _state.step;
          _saveState();
          _cleanSowQueryFromUrl();
          return true;
        }
      }
    } catch (e) {}
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        var saved = JSON.parse(raw);
        if (saved && saved.facilityType) {
          Object.assign(_state, saved);
          return true;
        }
      }
    } catch (e) {}
    return false;
  }

  function _buildAreaRow(area, index) {
    var floorOptions = SG._floorTypeNames().map(function (ft) {
      return '<option value="' + _esc(ft) + '"' + (ft === area.floorType ? ' selected' : '') + '>' + _esc(ft) + '</option>';
    }).join('');
    var restroom = isRestroomArea(area);
    var fixturesCell = restroom
      ? '<td><input type="number" class="ot-field__input sow-area-fixtures" data-field="fixtures" value="' + _esc(area.fixtures || '') + '" placeholder="#" min="0" step="1" aria-label="Fixture count" title="Restroom fixtures (toilets + urinals + sinks)" style="width:72px;" /></td>'
      : '<td class="sow-area-fixtures-na" aria-hidden="true">—</td>';
    return '<tr data-area-index="' + index + '">' +
      '<td><input type="text" class="ot-field__input sow-area-name" data-field="name" value="' + _esc(area.name) + '" placeholder="Area name" aria-label="Area name" /></td>' +
      '<td><input type="number" class="ot-field__input sow-area-sqft" data-field="sqft" value="' + _esc(area.sqft || '') + '" placeholder="sqft" min="0" step="1" aria-label="Area square footage" style="width:100px;" /></td>' +
      fixturesCell +
      '<td><select class="ot-field__select sow-area-floor" data-field="floorType" aria-label="Floor type">' + floorOptions + '</select></td>' +
      '<td><input type="text" class="ot-field__input sow-area-notes" data-field="notes" value="' + _esc(area.notes || '') + '" placeholder="Special considerations (optional)" aria-label="Special considerations" /></td>' +
      '<td><button type="button" class="sow-remove-area ot-actions__btn ot-actions__btn--reset" aria-label="Remove this area" data-remove-index="' + index + '" style="padding:4px 10px;font-size:12px;">Remove</button></td>' +
      '</tr>';
  }

  SG._floorTypeNames = function () {
    if (window.OPORA_TOOLS_DATA && window.OPORA_TOOLS_DATA.floor_types) {
      return window.OPORA_TOOLS_DATA.floor_types.map(function (f) { return f.name; });
    }
    return ['Carpet — commercial loop', 'VCT — vinyl composition tile', 'LVT / LVP (luxury vinyl)', 'Ceramic / porcelain tile', 'Sealed concrete (cured >28 d)', 'Natural stone — marble / limestone / travertine', 'Natural stone — granite', 'Hardwood — finished', 'Rubber sport / mat flooring', 'Epoxy / urethane coating'];
  };

  function _renderAreas() {
    var tbody = document.getElementById('sow-areas-tbody');
    if (!tbody) return;
    if (!_state.areas) { _state.areas = defaultAreasForFacilityType(_state.facilityType); }
    tbody.innerHTML = _state.areas.map(_buildAreaRow).join('');
    _wireAreaEvents();
    _updateSqftBalancer();
  }

  function _updateSqftBalancer() {
    var banner = document.getElementById('sow-sqft-balancer');
    var sumEl = document.getElementById('sow-areas-sum');
    var bal = sqftBalance(_state);
    var missingFx = restroomsMissingFixtures(_state.areas || []);
    if (sumEl) {
      sumEl.className = 'sow-areas-sum' + (bal.mismatch ? ' sow-areas-sum--bad' : '');
      sumEl.innerHTML = 'Areas sum: <strong>' + bal.areasSum.toLocaleString() +
        ' sq ft</strong> · Facility total: <strong>' + bal.facility.toLocaleString() + ' sq ft</strong>' +
        (bal.mismatch
          ? ' · <strong>out of balance by ' + Math.abs(bal.delta).toLocaleString() + ' sq ft</strong>'
          : (bal.areasWithSqft > 0 ? ' · balanced' : ' · enter area sq ft to reconcile')) +
        (missingFx.length
          ? ' · <strong class="sow-areas-sum__fx">restroom fixtures missing</strong> (' + missingFx.map(_esc).join(', ') + ')'
          : '');
    }
    if (!banner) return;
    if (!bal.mismatch) {
      if (bal.areasWithSqft > 0 && bal.facility > 0) {
        banner.hidden = false;
        banner.className = 'sow-sqft-balancer sow-sqft-balancer--ok';
        banner.innerHTML = '<p class="sow-sqft-balancer__msg">Area sq ft (' +
          bal.areasSum.toLocaleString() + ') matches facility total (' +
          bal.facility.toLocaleString() + ') within tolerance.' +
          (missingFx.length
            ? ' <strong>Add fixture counts</strong> for ' + missingFx.map(_esc).join(', ') +
              ' — fixtures drive restroom labor, not sq ft alone.'
            : '') +
          '</p>';
      } else if (missingFx.length) {
        banner.hidden = false;
        banner.className = 'sow-sqft-balancer sow-sqft-balancer--warn';
        banner.innerHTML = '<p class="sow-sqft-balancer__msg"><strong>Restroom fixtures:</strong> ' +
          missingFx.map(_esc).join(', ') +
          ' have sq ft but no fixture count. Fixture count drives restroom labor time — add counts before locking staffing.</p>';
      } else {
        banner.hidden = true;
        banner.innerHTML = '';
      }
      return;
    }
    banner.hidden = false;
    banner.className = 'sow-sqft-balancer sow-sqft-balancer--warn';
    var rel = bal.delta > 0 ? 'exceeds' : 'is under';
    banner.innerHTML =
      '<p class="sow-sqft-balancer__msg"><strong>Warning:</strong> Sum of Area Sq Ft (<strong>' +
      bal.areasSum.toLocaleString() + ' sq ft</strong>) ' + rel + ' Total Facility Sq Ft (<strong>' +
      bal.facility.toLocaleString() + ' sq ft</strong>) by ' +
      Math.abs(bal.delta).toLocaleString() +
      ' sq ft. Labor estimates use the facility total — reconcile before bidding.' +
      (missingFx.length
        ? ' Also add fixture counts for ' + missingFx.map(_esc).join(', ') + '.'
        : '') +
      '</p>' +
      '<div class="sow-sqft-balancer__actions">' +
      '<button type="button" class="sow-btn sow-btn--primary" id="sow-autoscale-areas">Auto-Scale Areas</button>' +
      '<button type="button" class="sow-btn sow-btn--ghost" id="sow-update-facility-total">Update Facility Total</button>' +
      '</div>';
    var scaleBtn = document.getElementById('sow-autoscale-areas');
    var updBtn = document.getElementById('sow-update-facility-total');
    if (scaleBtn) {
      scaleBtn.addEventListener('click', function () {
        autoScaleAreasToFacility(_state.areas, _state.totalSqft);
        _renderAreas();
        _saveState();
        _clearStepErrors();
      });
    }
    if (updBtn) {
      updBtn.addEventListener('click', function () {
        _state.totalSqft = String(Math.round(bal.areasSum));
        _setVal('sow-total-sqft', _state.totalSqft);
        _updateSqftBalancer();
        _saveState();
        _clearStepErrors();
      });
    }
  }

  function _wireAreaEvents() {
    var tbody = document.getElementById('sow-areas-tbody');
    if (!tbody) return;
    tbody.querySelectorAll('input, select').forEach(function (el) {
      el.addEventListener('input', function () {
        var tr = el.closest('tr');
        var idx = parseInt(tr.getAttribute('data-area-index'), 10);
        var field = el.getAttribute('data-field');
        var area = _state.areas[idx];
        if (!area) return;
        var prevName = area.name;
        area[field] = el.value;
        if (field === 'floorType' && isStoneFloorType(el.value)) {
          var notes = String(area.notes || '').trim();
          if (!notes) {
            area.notes = 'pH-neutral cleaner only (pH 7.0)';
            var notesInput = tr.querySelector('[data-field="notes"]');
            if (notesInput) notesInput.value = area.notes;
          }
        }
        _saveState();
        if (field === 'sqft' || field === 'fixtures') _updateSqftBalancer();
        // Floor-type changes re-filter tasks when user returns to step 3
        if (field === 'floorType') _state._tasksNeedSurfaceSync = true;
        // Restroom name toggle needs fixtures column re-render
        if (field === 'name' && isRestroomArea({ name: prevName }) !== isRestroomArea(area)) {
          _renderAreas();
        }
      });
    });
    tbody.querySelectorAll('[data-remove-index]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(btn.getAttribute('data-remove-index'), 10);
        _state.areas.splice(idx, 1);
        _state._tasksNeedSurfaceSync = true;
        _renderAreas();
      });
    });
  }

  function _syncTasksToSurfaces(force) {
    if (!_state.frequencyMatrix) {
      _state.frequencyMatrix = defaultFrequencyMatrix(_state.facilityType);
      force = true;
    }
    if (!force && !_state._tasksNeedSurfaceSync) return;
    _state.frequencyMatrix = filterTasksForSurfaces(_state.frequencyMatrix, _state.areas || []);
    _state._tasksNeedSurfaceSync = false;
  }

  function _renderFrequencyMatrix() {
    _syncTasksToSurfaces(true);
    var freqs = ['daily', 'weekly', 'monthly', 'quarterly', 'annual'];
    freqs.forEach(function (freq) {
      var container = document.getElementById('sow-freq-' + freq);
      if (!container) return;
      var tasks = _state.frequencyMatrix[freq] || [];
      container.innerHTML = tasks.map(function (task, i) {
        return '<div class="sow-freq-task"><input type="text" class="ot-field__input sow-freq-task-input" value="' + _esc(task) + '" data-freq="' + freq + '" data-idx="' + i + '" aria-label="' + freq + ' task ' + (i + 1) + '" style="flex:1;" /><button type="button" class="sow-remove-task ot-actions__btn ot-actions__btn--reset" aria-label="Remove task" data-freq="' + freq + '" data-idx="' + i + '" style="padding:4px 8px;font-size:11px;flex-shrink:0;">Remove</button></div>';
      }).join('');
      _wireFreqEvents(container, freq);
    });
  }

  function _wireFreqEvents(container, freq) {
    container.querySelectorAll('.sow-freq-task-input').forEach(function (el) {
      el.addEventListener('input', function () {
        var idx = parseInt(el.getAttribute('data-idx'), 10);
        _state.frequencyMatrix[freq][idx] = el.value;
        _saveState();
      });
    });
    container.querySelectorAll('.sow-remove-task').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(btn.getAttribute('data-idx'), 10);
        _state.frequencyMatrix[freq].splice(idx, 1);
        _renderFrequencyMatrix();
      });
    });
  }

  function _trackSow(name, props) {
    var payload = Object.assign({ tool: 'scope-of-work-generator' }, props || {});
    try {
      if (typeof window.oporaTrack === 'function') window.oporaTrack(name, payload);
    } catch (e0) {}
    try {
      if (typeof gtag === 'function') gtag('event', name, payload);
    } catch (e1) {}
  }

  function _toast(msg, type) {
    try {
      if (window.OporaTools && typeof window.OporaTools.showToast === 'function') {
        window.OporaTools.showToast(msg, type || 'success');
        return;
      }
    } catch (e0) {}
    try {
      var t = document.createElement('div');
      t.setAttribute('role', 'status');
      t.setAttribute('aria-live', 'polite');
      t.textContent = String(msg || '');
      Object.assign(t.style, {
        position: 'fixed',
        left: '50%',
        bottom: '110px',
        transform: 'translateX(-50%)',
        zIndex: '10060',
        padding: '12px 18px',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        fontFamily: 'Inter, system-ui, sans-serif',
        maxWidth: '360px',
        textAlign: 'center',
        background: type === 'error' ? '#C0392B' : '#01696f',
        color: '#fff',
        boxShadow: '0 8px 28px rgba(20,18,14,.22)',
        transition: 'opacity .3s',
        opacity: '1'
      });
      document.body.appendChild(t);
      setTimeout(function () {
        t.style.opacity = '0';
        setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 350);
      }, 2800);
    } catch (e1) {}
  }

  function _setDockStatus(label) {
    var el = document.querySelector('#sow-ab-mount .opora-ab__status .opora-ab__label');
    if (el) el.textContent = label;
  }

  function _draftEditUrl() {
    try {
      var fullState = JSON.parse(JSON.stringify(_state));
      var encoded = serializeState(fullState);
      var url = new URL(window.location.href);
      url.searchParams.set('sow', encoded);
      url.searchParams.set('sow_return', '1');
      url.hash = '';
      return url.toString();
    } catch (e) {
      return window.location.href.split('#')[0];
    }
  }

  function _makeSheet() {
    var sheet = document.createElement('article');
    sheet.className = 'sow-sheet';
    sheet.setAttribute('aria-label', 'Document page');
    var inner = document.createElement('div');
    inner.className = 'sow-sheet__inner';
    var footer = document.createElement('div');
    footer.className = 'sow-sheet__footer';
    sheet.appendChild(inner);
    sheet.appendChild(footer);
    return { sheet: sheet, inner: inner, footer: footer };
  }

  function _paginatePreview(preview, blocks, facilityLabel) {
    preview.innerHTML = '';
    preview.classList.add('sow-paper-canvas');

    var measureHost = document.createElement('div');
    measureHost.className = 'sow-measure-host';
    var probe = _makeSheet();
    measureHost.appendChild(probe.sheet);
    document.body.appendChild(measureHost);

    var sheets = [];
    var current = _makeSheet();
    preview.appendChild(current.sheet);
    sheets.push(current);

    var maxH = Math.max(120, probe.inner.clientHeight || Math.round(816 * (11 / 8.5) * 0.82));

    function fits() {
      return current.inner.scrollHeight <= maxH + 2;
    }

    blocks.forEach(function (node) {
      current.inner.appendChild(node);
      if (sheets.length === 1 && current.inner.childNodes.length === 1) return;
      if (!fits()) {
        current.inner.removeChild(node);
        if (!current.inner.childNodes.length) {
          /* Oversized block — keep on its own page rather than clip forever */
          current.inner.appendChild(node);
          return;
        }
        current = _makeSheet();
        preview.appendChild(current.sheet);
        sheets.push(current);
        current.inner.appendChild(node);
      }
    });

    var total = sheets.length;
    var LEGAL_SHORT = 'Not legal advice — review with counsel before signature.';
    sheets.forEach(function (s, i) {
      var page = i + 1;
      var micro = _esc(facilityLabel || 'Scope of Work') + ' — Scope of Work · Page ' + page + ' of ' + total;
      var showLegal = (page === 1 || page === total);
      s.footer.innerHTML =
        '<div class="sow-sheet__footer-row">' +
          '<span class="sow-sheet__footer-micro">' + micro + '</span>' +
        '</div>' +
        (showLegal ? '<div class="sow-sheet__footer-legal">' + _esc(LEGAL_SHORT) + '</div>' : '');
      s.sheet.setAttribute('aria-label', 'Page ' + page + ' of ' + total);
      s.sheet.setAttribute('data-page', String(page));
      s.sheet.setAttribute('data-pages', String(total));
    });

    try { document.body.removeChild(measureHost); } catch (eM) {}
    return total;
  }

  function _execCardHtml(doc) {
    var sqft = doc.totalSqft ? Number(doc.totalSqft).toLocaleString() : 'TBD';
    var floors = String(doc.numFloors || _state.numFloors || 1);
    return '<div class="sow-exec-card">' +
      '<div class="sow-exec-card__cell"><span>Facility type</span><strong>' + _esc(doc.facilityType || '—') + '</strong></div>' +
      '<div class="sow-exec-card__cell"><span>Total sq ft</span><strong>' + _esc(sqft) + '</strong></div>' +
      '<div class="sow-exec-card__cell"><span>Floors</span><strong>' + _esc(floors) + '</strong></div>' +
      '<div class="sow-exec-card__cell"><span>Generated</span><strong>' + _esc(doc.generatedDate || '') + '</strong></div>' +
      '</div>';
  }

  function _renderPreview() {
    var preview = document.getElementById('sow-preview-content');
    var statusPill = document.getElementById('sow-status-pill');
    if (!preview) return;
    var exclTextarea = document.getElementById('sow-exclusions-text');
    if (exclTextarea) { _state.exclusionsList = exclTextarea.value; }
    var stateForDoc = Object.assign({}, _state, {
      exclusions: _state.exclusionsList ? _state.exclusionsList.split('\n').map(function (l) { return l.trim(); }).filter(Boolean) : null
    });
    var doc = buildScopeDocument(stateForDoc);
    var isComplete = !!(_state.facilityName && _state.facilityName.trim() && Number(_state.totalSqft) > 0);
    if (statusPill) {
      statusPill.className = 'ot-pill ' + (isComplete ? 'ot-pill--safe' : 'ot-pill--caution');
      var lbl = statusPill.querySelector('.ot-pill__label');
      if (lbl) lbl.textContent = isComplete ? 'Document looks complete' : 'Facility name or sqft missing';
    }

    var jumpTargets = [];
    var statusClass = doc.documentStatus === 'CONTRACT READY' ? ' sow-doc-status-badge--ready' : '';
    var html = '<article class="sow-doc">';
    html += '<div class="sow-doc__cover">';
    html += '<p class="sow-doc__cover-kicker">Commercial Cleaning Services Specification</p>';
    html += '<div class="sow-doc__cover-row">';
    html += '<h1 class="sow-doc__cover-title">Scope of Work</h1>';
    html += '<span class="sow-doc-status-badge' + statusClass + '">' + _esc(doc.documentStatus || 'EXHIBIT — DRAFT') + '</span>';
    html += '</div></div>';
    html += '<h2 class="sow-doc__facility">' + _esc(doc.facilityName || 'Facility') + '</h2>';
    html += _execCardHtml(doc);

    doc.sections.forEach(function (s, idx) {
      var sid = 'sow-sec-' + (idx + 1);
      var short = String(s.title || '').replace(/^\d+\.\s*/, '');
      if (idx === 2 || idx === 7 || idx === 14 || /areas|quality|sign/i.test(short)) {
        jumpTargets.push({ id: sid, label: short.split(/[:—(]/)[0].trim().slice(0, 28) });
      }
      var bodyHtml;
      if (idx === 1) bodyHtml = _hoursSectionHtml(doc);
      else if (idx === 2) bodyHtml = _areasSectionHtml(doc);
      else if (idx === 14) bodyHtml = _signatureSectionHtml(doc);
      else bodyHtml = _contentToHtml(s.content);
      html += '<section class="sow-doc-section" id="' + sid + '">';
      html += '<h2 class="sow-doc-section__title">' + _esc(s.title) + '</h2>';
      html += '<div class="sow-doc-section__body">' + bodyHtml + '</div></section>';
    });

    html += '<footer class="sow-doc__endnote">Not legal advice — review with counsel before signature.</footer>';
    html += '</article>';

    preview.className = 'sow-paper-canvas';
    preview.innerHTML = html;
    _renderToc(jumpTargets);

    SG._lastDoc = doc;
    SG._lastState = _state;

    var areasOk = Array.isArray(_state.areas) && _state.areas.length > 0;
    if (areasOk && !SG._previewTracked) {
      SG._previewTracked = true;
      _trackSow('sow_preview_rendered', {
        facility_type: _state.facilityType || '',
        num_areas: _state.areas.length,
        quality_level: _state.qualityLevel || 'APPA 2'
      });
    }
  }

  function _renderToc(targets) {
    var toc = document.getElementById('sow-toc');
    if (!toc) return;
    if (!targets || !targets.length) {
      toc.hidden = true;
      toc.innerHTML = '';
      return;
    }
    var seen = {};
    var bits = ['<span class="sow-toc__label">Jump to:</span>'];
    targets.forEach(function (t, i) {
      if (!t.label || seen[t.label]) return;
      seen[t.label] = true;
      if (bits.length > 1) bits.push('<span class="sow-toc__sep" aria-hidden="true">·</span>');
      bits.push('<a class="sow-toc__link" href="#' + t.id + '">' + _esc(t.label) + '</a>');
    });
    toc.innerHTML = bits.join(' ');
    toc.hidden = bits.length < 3;
  }

  function _contentToHtml(text) {
    return String(text || '').split('\n').map(function (line) {
      if (!line.trim()) return '<br />';
      var trimmed = line.replace(/^\s+/, '');
      var isBullet = /^[\u2022•\-\*]\s+/.test(trimmed);
      var body = isBullet ? trimmed.replace(/^[\u2022•\-\*]\s+/, '') : trimmed;
      var escaped = _esc(body).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      if (isBullet) {
        return '<p class="sow-doc-para sow-doc-para--bullet"><span class="sow-doc-bullet" aria-hidden="true">•</span><span class="sow-doc-bullet-text">' + escaped + '</span></p>';
      }
      return '<p class="sow-doc-para">' + escaped + '</p>';
    }).join('');
  }

  function _laborCalloutHtml(labor) {
    if (!labor) {
      return '<aside class="sow-callout">' +
        '<h3 class="sow-callout__title">Estimated cleaning labor</h3>' +
        '<p class="sow-callout__note">Open the Production Rate Calculator with this facility sq ft to attach hours/visit to the bid. Walk the building before locking price.</p>' +
        '</aside>';
    }
    return '<aside class="sow-callout sow-callout--metrics">' +
      '<h3 class="sow-callout__title">Estimated cleaning labor — APPA ' + _esc(String(labor.appa)) + ' · Nightly 5×/week</h3>' +
      '<div class="sow-callout__statgrid">' +
      '<div><span>Hours / visit</span><strong>' + labor.nightly_hours.toFixed(2) + ' hr</strong></div>' +
      '<div><span>Weekly</span><strong>' + labor.weekly_hours.toFixed(1) + ' hr</strong></div>' +
      '<div><span>Annual</span><strong>' + Math.round(labor.annual_hours).toLocaleString() + ' hr</strong></div>' +
      '<div><span>FTE</span><strong>~' + labor.fte.toFixed(2) + '</strong></div>' +
      '<div><span>Blended</span><strong>' + Math.round(labor.blended_sf_per_hr).toLocaleString() + ' sf/hr</strong></div>' +
      '</div>' +
      '<p class="sow-callout__note">Planning estimates — walk the building before locking price.</p>' +
      '</aside>';
  }

  function _hoursSectionHtml(doc) {
    var section = doc && doc.sections && doc.sections[1];
    var raw = (section && section.content) || '';
    var parts = raw.split(/\n\nEstimated cleaning labor/);
    return _contentToHtml(parts[0] || '') + _laborCalloutHtml(doc && doc.labor);
  }

  function _areasSectionHtml(doc) {
    var areas = (doc && doc.areas) || _state.areas || [];
    var floors = Number((doc && doc.numFloors) || _state.numFloors) || 1;
    var total = Number((doc && doc.totalSqft) || _state.totalSqft) || 0;
    var areasSum = sumAreaSqft(areas);
    var bal = sqftBalance({ totalSqft: total, areas: areas });
    var missingFx = restroomsMissingFixtures(areas);

    var html = '<p class="sow-doc-para">The following areas are included in the scope of recurring cleaning services. Square footages are approximate and may be verified by Contractor during a pre-bid walk-through.</p>';
    html += '<table class="sow-doc-table"><thead><tr>' +
      '<th scope="col">Area Name</th>' +
      '<th scope="col" class="sow-doc-table__sqft">Approx. Sq Ft</th>' +
      '<th scope="col">Floor Type</th>' +
      '<th scope="col" class="sow-doc-table__notes">Notes</th>' +
      '</tr></thead><tbody>';
    if (!areas.length) {
      html += '<tr><td colspan="4">No areas listed.</td></tr>';
    } else {
      areas.forEach(function (a) {
        var sqftNum = parseAreaSqft(a && a.sqft);
        var sqft = sqftNum ? sqftNum.toLocaleString() + ' sq ft' : 'sq ft to be confirmed';
        var fx = parseFixtures(a && a.fixtures);
        var notes = String((a && a.notes) || '');
        if (isRestroomArea(a) && fx > 0) {
          notes = (fx + ' fixture' + (fx === 1 ? '' : 's')) + (notes ? ' · ' + notes : '');
        }
        html += '<tr>' +
          '<td>' + _esc((a && a.name) || '—') + '</td>' +
          '<td class="sow-doc-table__sqft">' + _esc(sqft) + '</td>' +
          '<td>' + _esc((a && a.floorType) || 'TBD') + '</td>' +
          '<td class="sow-doc-table__notes">' + _esc(notes) + '</td>' +
          '</tr>';
      });
    }
    html += '</tbody></table>';

    var reconcile = 'Total facility: approximately ' + (total ? total.toLocaleString() : 'to be confirmed') +
      ' sq ft across ' + floors + ' floor' + (floors !== 1 ? 's' : '') + '.';
    if (areasSum > 0) {
      reconcile += ' Sum of listed areas: ' + areasSum.toLocaleString() + ' sq ft.';
    }
    html += '<p class="sow-doc-para">' + _esc(reconcile) + '</p>';
    if (bal.mismatch) {
      html += '<p class="sow-doc-para sow-doc-para--warn"><strong>WARNING:</strong> Area line items do not reconcile to the facility total — verify sq ft before bidding. Labor staffing uses the facility total unless you update it.</p>';
    }
    if (missingFx.length) {
      html += '<p class="sow-doc-para sow-doc-para--warn"><strong>NOTE:</strong> Restroom fixture counts are unpopulated for ' +
        _esc(missingFx.join(', ')) +
        '. Fixture count drives restroom labor time — add counts before locking staffing.</p>';
    }
    html += '<p class="sow-doc-para">Areas not listed above are excluded from recurring service unless added by written addendum signed by both parties.</p>';
    return html;
  }

  function _sigField(label, value) {
    var filled = value ? '<span class="sow-sig__value">' + _esc(value) + '</span>' : '<span class="sow-sig__line" aria-hidden="true"></span>';
    return '<div class="sow-sig__row"><span class="sow-sig__label">' + _esc(label) + '</span>' + filled + '</div>';
  }

  function _signatureSectionHtml(doc) {
    var org = (doc && doc.facilityName) || (_state && _state.facilityName) || '';
    var html = '';
    html += '<p class="sow-doc-para">By signing below, the authorized representatives of both parties agree that this Scope of Work accurately reflects the services to be provided and the standards to be maintained.</p>';
    html += '<div class="sow-sig-grid">';
    html += '<div class="sow-sig-card">';
    html += '<h3 class="sow-sig-card__title">Facility Owner / Manager</h3>';
    html += _sigField('Company', org || '');
    html += _sigField('Name', '');
    html += _sigField('Title', '');
    html += _sigField('Date', '');
    html += _sigField('Signature', '');
    html += '</div>';
    html += '<div class="sow-sig-card">';
    html += '<h3 class="sow-sig-card__title">Contractor</h3>';
    html += _sigField('Company', '');
    html += _sigField('Name', '');
    html += _sigField('Title', '');
    html += _sigField('Date', '');
    html += _sigField('Signature', '');
    html += '</div>';
    html += '</div>';

    html += '<p class="sow-doc-para sow-doc-para--muted">' + _esc(EXHIBITS_NOTE) + '</p>';
    return html;
  }

  function _esc(str) {
    return String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function _advance() {
    if (_state.step >= MAX_STEP) return;
    if (_state.step === 2) _updateSqftBalancer();
    var result = validateStep(_state.step, _state);
    if (!result.valid) {
      _showStepErrors(result.errors);
      if (_state.step === 2) _updateSqftBalancer();
      return;
    }
    _clearStepErrors();
    if (_state.step === 2) _state._tasksNeedSurfaceSync = true;
    _showStep(_state.step + 1);
  }

  function _back() {
    if (_state.step <= 1) return;
    _clearStepErrors();
    _showStep(_state.step - 1);
  }

  function _showStepErrors(errors) {
    var errBox = document.querySelector('.sow-step-errors');
    if (!errBox) return;
    errBox.innerHTML = errors.map(function (e) { return '<p>' + _esc(e) + '</p>'; }).join('');
    errBox.style.display = '';
  }

  function _clearStepErrors() {
    var errBox = document.querySelector('.sow-step-errors');
    if (!errBox) return;
    errBox.innerHTML = '';
    errBox.style.display = 'none';
  }

  /**
   * Version-3 jsPDF engine (rolled back from html2canvas) + world-class polish:
   * cover band, executive meta card, native areas table, labor metrics callout,
   * signature grid, exhibits module. Preview HTML mirrors the same structure.
   */
  SG.downloadPDF = function () {
    if (!_state.frequencyMatrix) _state.frequencyMatrix = defaultFrequencyMatrix(_state.facilityType);
    if (!_state.areas) _state.areas = defaultAreasForFacilityType(_state.facilityType);
    var exclTextarea = document.getElementById('sow-exclusions-text');
    if (exclTextarea) {
      exclTextarea.value = _normalizeExclusionsText(exclTextarea.value);
      _state.exclusionsList = exclTextarea.value;
    }
    var stateForDoc = Object.assign({}, _state, {
      exclusions: _state.exclusionsList
        ? _state.exclusionsList.split('\n').map(function (l) { return l.trim(); }).filter(Boolean)
        : null
    });
    var doc = buildScopeDocument(stateForDoc);
    SG._lastDoc = doc;

    if (typeof window.jspdf === 'undefined' || !window.jspdf.jsPDF) {
      _toast('PDF library not loaded. Refresh and try again.', 'error');
      return Promise.reject(new Error('jsPDF missing'));
    }

    var jsPDF = window.jspdf.jsPDF;
    var pdf = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait' });
    var pageWidth = pdf.internal.pageSize.getWidth();
    var pageHeight = pdf.internal.pageSize.getHeight();
    var marginL = 50, marginR = 50, marginT = 48, marginB = 58;
    var contentWidth = pageWidth - marginL - marginR;
    var y = marginT;
    var pageNum = 1;
    var BRAND = [15, 122, 112];
    var BRAND_LIGHT = [232, 245, 243];
    var SLATE_BG = [248, 250, 252];
    var SLATE_BD = [226, 232, 240];
    var TEXT = [26, 24, 22];
    var MUTED = [92, 88, 84];
    var FOOT_GRAY = [148, 163, 184]; /* #94A3B8 */
    var DISCLAIMER = 'Not legal advice - review with counsel before signature.';
    var docStatus = doc.documentStatus || 'EXHIBIT - DRAFT';
    var isReady = docStatus === 'CONTRACT READY';

    /* Helvetica/WinAnsi drops or blanks many Unicode glyphs (en-dash, smart quotes).
       Also strip accidental \$ escapes some pipelines inject before $ amounts. */
    function _pdfSafeText(s) {
      return String(s == null ? '' : s)
        .replace(/\\\$/g, '$')
        .replace(/[\u2013\u2014\u2212]/g, '-')
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[\u201C\u201D]/g, '"')
        .replace(/\u00A0/g, ' ')
        .replace(/\u2026/g, '...');
    }

    function _pdfText(str, x, yPt, opts) {
      if (opts) pdf.text(_pdfSafeText(str), x, yPt, opts);
      else pdf.text(_pdfSafeText(str), x, yPt);
    }

    function _addHeader() {
      pdf.setFillColor(BRAND[0], BRAND[1], BRAND[2]);
      pdf.rect(0, 0, pageWidth, 4, 'F');
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8); pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
      _pdfText(String(doc.facilityName || 'Scope of Work'), marginL, 26);
      _pdfText('Scope of Work', pageWidth / 2, 26, { align: 'center' });
      /* Page numbers live in the footer only (Page X of Y) — avoid dual counts */
      pdf.setDrawColor(BRAND[0], BRAND[1], BRAND[2]); pdf.setLineWidth(0.5);
      pdf.line(marginL, 32, pageWidth - marginR, 32);
      pdf.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
    }

    function _addFooter(totalPagesOpt) {
      var totalPages = totalPagesOpt || pdf.internal.getNumberOfPages();
      var footerY = pageHeight - 28;
      pdf.setDrawColor(226, 232, 240); pdf.setLineWidth(0.4);
      pdf.line(marginL, footerY - 12, pageWidth - marginR, footerY - 12);
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5); pdf.setTextColor(FOOT_GRAY[0], FOOT_GRAY[1], FOOT_GRAY[2]);
      _pdfText(
        String(doc.facilityName || '') + ' - Scope of Work  ·  Page ' + pageNum + ' of ' + totalPages,
        marginL,
        footerY - 2
      );
      /* Subtle disclaimer only — no platform branding in the PDF */
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(FOOT_GRAY[0], FOOT_GRAY[1], FOOT_GRAY[2]);
      _pdfText(DISCLAIMER, marginL, pageHeight - 14);
    }

    function _newPage() {
      /* Footers drawn once at end (avoids stale disclaimer when page was temporarily last) */
      pdf.addPage();
      pageNum++;
      y = marginT;
      _addHeader();
    }

    function _ensureSpace(needed) {
      if (y + needed > pageHeight - marginB) _newPage();
    }

    function _sectionTitle(title) {
      _ensureSpace(40);
      pdf.setFillColor(BRAND[0], BRAND[1], BRAND[2]);
      pdf.rect(marginL, y, 3, 14, 'F');
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
      _pdfText(title, marginL + 10, y + 11);
      /* Rule sits a bit below the title */
      y += 18;
      pdf.setDrawColor(232, 230, 225); pdf.setLineWidth(0.6);
      pdf.line(marginL, y, pageWidth - marginR, y);
      /* Breath below the rule, before body copy */
      y += 11;
    }

    function _writeParagraph(text, fontSize, bold, indent) {
      pdf.setFont('helvetica', bold ? 'bold' : 'normal');
      pdf.setFontSize(fontSize || 9.5);
      pdf.setTextColor(52, 50, 48);
      var x = marginL + (indent || 0);
      var w = contentWidth - (indent || 0);
      var safe = _pdfSafeText(text);
      var lines = pdf.splitTextToSize(safe, w);
      var lineH = (fontSize || 9.5) * 1.35;
      _ensureSpace(lines.length * lineH + 2);
      pdf.text(lines, x, y);
      /* Clear last baseline; block writer owns inter-paragraph rhythm */
      y += lines.length * lineH + 2;
    }

    function _writeBullets(lines) {
      (lines || []).forEach(function (line) {
        if (!line || !String(line).trim()) return;
        var text = _pdfSafeText(String(line).trim().replace(/^[\u2022•\-\*]\s*/, ''));
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9.5); pdf.setTextColor(52, 50, 48);
        var wrapped = pdf.splitTextToSize(text, contentWidth - 18);
        var lineH = 9.5 * 1.32;
        _ensureSpace(wrapped.length * lineH + 2);
        pdf.setFillColor(BRAND[0], BRAND[1], BRAND[2]);
        pdf.circle(marginL + 4, y - 2.5, 1.4, 'F');
        pdf.text(wrapped, marginL + 14, y);
        y += wrapped.length * lineH + 2;
      });
    }

    function _formatSqft(val) {
      var n = Number(String(val || '').replace(/[^\d.]/g, ''));
      return n ? n.toLocaleString() + ' sq ft' : 'TBD';
    }

    function _drawStatusBadge(xRight, yMid) {
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7.5);
      var label = docStatus;
      var tw = pdf.getTextWidth(label);
      var bw = tw + 14, bh = 14;
      var bx = xRight - bw, by = yMid - bh / 2;
      if (isReady) {
        pdf.setFillColor(BRAND[0], BRAND[1], BRAND[2]);
        pdf.setTextColor(255, 255, 255);
      } else {
        pdf.setFillColor(245, 240, 230);
        pdf.setDrawColor(180, 160, 120);
        pdf.setLineWidth(0.5);
        pdf.roundedRect(bx, by, bw, bh, 2, 2, 'FD');
        pdf.setTextColor(120, 90, 40);
        _pdfText(label, bx + 7, yMid + 2.5);
        pdf.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
        return;
      }
      pdf.roundedRect(bx, by, bw, bh, 2, 2, 'F');
      _pdfText(label, bx + 7, yMid + 2.5);
      pdf.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
    }

    function _drawExecCard() {
      var cardH = 62;
      _ensureSpace(cardH + 12);
      pdf.setFillColor(SLATE_BG[0], SLATE_BG[1], SLATE_BG[2]);
      pdf.setDrawColor(SLATE_BD[0], SLATE_BD[1], SLATE_BD[2]);
      pdf.setLineWidth(0.8);
      if (typeof pdf.roundedRect === 'function') pdf.roundedRect(marginL, y, contentWidth, cardH, 4, 4, 'FD');
      else pdf.rect(marginL, y, contentWidth, cardH, 'FD');

      var colW = contentWidth / 2;
      var rows = [
        [
          { l: 'Facility type', v: doc.facilityType || '—' },
          { l: 'Total sq ft', v: doc.totalSqft ? Number(doc.totalSqft).toLocaleString() : 'TBD' }
        ],
        [
          { l: 'Floors', v: String(doc.numFloors || _state.numFloors || 1) },
          { l: 'Generated', v: doc.generatedDate || '' }
        ]
      ];
      var ry = y + 16;
      rows.forEach(function (pair) {
        pair.forEach(function (cell, ci) {
          var cx = marginL + 14 + ci * colW;
          pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5); pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
          _pdfText(cell.l.toUpperCase(), cx, ry);
          pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10); pdf.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
          _pdfText(String(cell.v), cx, ry + 13);
        });
        ry += 26;
      });
      y += cardH + 10;
    }

    function _drawLaborCallout(labor) {
      if (!labor) {
        _ensureSpace(48);
        pdf.setFillColor(SLATE_BG[0], SLATE_BG[1], SLATE_BG[2]);
        pdf.setDrawColor(SLATE_BD[0], SLATE_BD[1], SLATE_BD[2]);
        if (typeof pdf.roundedRect === 'function') pdf.roundedRect(marginL, y, contentWidth, 40, 4, 4, 'FD');
        else pdf.rect(marginL, y, contentWidth, 40, 'FD');
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.setTextColor(BRAND[0], BRAND[1], BRAND[2]);
        _pdfText('Estimated cleaning labor', marginL + 12, y + 16);
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
        _pdfText('Open Production Rate Calculator to attach hours before locking price.', marginL + 12, y + 30);
        y += 40;
        return;
      }
      var cardH = 78;
      _ensureSpace(cardH + 8);
      pdf.setFillColor(BRAND_LIGHT[0], BRAND_LIGHT[1], BRAND_LIGHT[2]);
      pdf.setDrawColor(197, 228, 223);
      pdf.setLineWidth(0.8);
      if (typeof pdf.roundedRect === 'function') pdf.roundedRect(marginL, y, contentWidth, cardH, 4, 4, 'FD');
      else pdf.rect(marginL, y, contentWidth, cardH, 'FD');
      pdf.setFillColor(BRAND[0], BRAND[1], BRAND[2]);
      pdf.rect(marginL, y, 4, cardH, 'F');

      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.setTextColor(BRAND[0], BRAND[1], BRAND[2]);
      _pdfText('Estimated cleaning labor - APPA ' + labor.appa + ' · Nightly 5x/week', marginL + 14, y + 16);

      var metrics = [
        { l: 'Hours / visit', v: labor.nightly_hours.toFixed(2) + ' hr' },
        { l: 'Weekly', v: labor.weekly_hours.toFixed(1) + ' hr' },
        { l: 'Annual', v: Math.round(labor.annual_hours).toLocaleString() + ' hr' },
        { l: 'FTE', v: '~' + labor.fte.toFixed(2) },
        { l: 'Blended', v: Math.round(labor.blended_sf_per_hr).toLocaleString() + ' sf/hr' }
      ];
      var mw = (contentWidth - 28) / metrics.length;
      metrics.forEach(function (m, i) {
        var cx = marginL + 14 + i * mw;
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7); pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
        _pdfText(m.l.toUpperCase(), cx, y + 34);
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10); pdf.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
        _pdfText(m.v, cx, y + 48);
      });
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5); pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
      _pdfText('Planning estimates - walk the building before locking price.', marginL + 14, y + 66);
      y += cardH + 8;
    }

    function _drawAreasTable(areas) {
      /* Floor Type gets the widest column — long stone/carpet labels wrap less */
      var cols = [
        { label: 'Area Name', w: contentWidth * 0.24 },
        { label: 'Approx. Sq Ft', w: contentWidth * 0.14 },
        { label: 'Floor Type', w: contentWidth * 0.42 },
        { label: 'Notes', w: contentWidth * 0.20 }
      ];
      var pad = 6;
      var headerH = 20;
      var minRowH = 22; /* keep header with at least one data row */

      function colX(idx) {
        var x = marginL;
        for (var i = 0; i < idx; i++) x += cols[i].w;
        return x;
      }
      function drawTableHeader() {
        /* Never orphan a header alone at the bottom of a page */
        _ensureSpace(headerH + minRowH + 8);
        pdf.setFillColor(BRAND[0], BRAND[1], BRAND[2]);
        pdf.rect(marginL, y, contentWidth, headerH, 'F');
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8); pdf.setTextColor(255, 255, 255);
        cols.forEach(function (c, i) { _pdfText(c.label, colX(i) + pad, y + 13); });
        y += headerH;
      }

      drawTableHeader();
      (areas || []).forEach(function (area, rowIdx) {
        var fx = parseFixtures(area && area.fixtures);
        var notesCell = String(area.notes || '');
        if (isRestroomArea(area) && fx > 0) {
          notesCell = fx + ' fixture' + (fx === 1 ? '' : 's') + (notesCell ? ' · ' + notesCell : '');
        }
        if (!notesCell) notesCell = '-';
        var cells = [
          _pdfSafeText(area.name || '-'),
          _formatSqft(area.sqft),
          _pdfSafeText(area.floorType || 'TBD'),
          _pdfSafeText(notesCell)
        ];
        var cellLines = cells.map(function (text, i) {
          return pdf.splitTextToSize(String(text), cols[i].w - pad * 2);
        });
        var rowH = Math.max.apply(null, cellLines.map(function (lines) { return lines.length; })) * 11 + pad * 2;
        if (y + rowH > pageHeight - marginB) {
          _newPage();
          drawTableHeader();
        }
        if (rowIdx % 2 === 0) {
          pdf.setFillColor(BRAND_LIGHT[0], BRAND_LIGHT[1], BRAND_LIGHT[2]);
          pdf.rect(marginL, y, contentWidth, rowH, 'F');
        }
        pdf.setDrawColor(180, 190, 200); pdf.setLineWidth(0.6);
        pdf.rect(marginL, y, contentWidth, rowH, 'S');
        var cy = y + pad + 8;
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(52, 50, 48);
        cellLines.forEach(function (lines, i) {
          pdf.text(lines, colX(i) + pad, cy);
          if (i > 0) {
            pdf.setDrawColor(180, 190, 200);
            pdf.line(colX(i), y, colX(i), y + rowH);
          }
        });
        y += rowH;
      });
      y += 10;
    }

    function _drawSignatureBlock(facilityName) {
      var gap = 14;
      var cardW = (contentWidth - gap) / 2;
      var cardH = 118;
      var pad = 8;
      var fields = ['Company', 'Name', 'Title', 'Date', 'Signature'];
      _ensureSpace(cardH + 14);

      function drawCard(x, title, companyVal) {
        /* Hairline native vector — no fill / no roundedRect (cleaner print) */
        pdf.setDrawColor(52, 50, 48);
        pdf.setLineWidth(0.6);
        pdf.rect(x, y, cardW, cardH, 'S');
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8); pdf.setTextColor(BRAND[0], BRAND[1], BRAND[2]);
        _pdfText(title, x + pad, y + 14);
        pdf.setDrawColor(180, 175, 168);
        pdf.setLineWidth(0.4);
        pdf.line(x + pad, y + 18, x + cardW - pad, y + 18);
        var rowY = y + 32;
        fields.forEach(function (label) {
          pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8); pdf.setTextColor(52, 50, 48);
          _pdfText(label + ':', x + pad, rowY);
          var lineX = x + pad + 48;
          pdf.setDrawColor(52, 50, 48);
          pdf.setLineWidth(0.45);
          pdf.line(lineX, rowY + 1, x + cardW - pad, rowY + 1);
          if (label === 'Company' && companyVal) {
            pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(52, 50, 48);
            _pdfText(String(companyVal).slice(0, 34), lineX, rowY - 1);
          }
          rowY += 16;
        });
      }
      drawCard(marginL, 'FACILITY OWNER / MANAGER', facilityName);
      drawCard(marginL + cardW + gap, 'CONTRACTOR', '');
      y += cardH + 12;
    }

    function _drawExhibitsNote() {
      _writeParagraph(EXHIBITS_NOTE, 8.5, false);
      y += 4;
    }

    function _writeSectionBody(section) {
      /* Split on blank lines into blocks so gaps are applied once — not
         paragraph-trailing + blank-line stacked (the “ton of whitespace”). */
      var blocks = String(section.content || '').split(/\n\s*\n/);
      var bi;
      for (bi = 0; bi < blocks.length; bi++) {
        var block = String(blocks[bi] || '').trim();
        if (!block) continue;
        if (bi > 0) y += 6; /* single controlled gap between blocks */
        var lines = block.split('\n');
        var li = 0;
        while (li < lines.length) {
          var trimmed = String(lines[li] || '').trim().replace(/\*\*/g, '');
          if (!trimmed) { li++; continue; }
          var isBullet = trimmed.charAt(0) === '\u2022' || trimmed.charAt(0) === '•' || /^[\-\*]\s/.test(trimmed);
          if (isBullet) {
            var bullets = [];
            while (li < lines.length) {
              var t = String(lines[li] || '').trim().replace(/\*\*/g, '');
              if (!t) { li++; break; }
              if (!(t.charAt(0) === '\u2022' || t.charAt(0) === '•' || /^[\-\*]\s/.test(t))) break;
              bullets.push(t);
              li++;
            }
            y += 2;
            _writeBullets(bullets);
            continue;
          }
          if (/^\d+\.\s/.test(trimmed) || (trimmed.indexOf(':') === trimmed.length - 1 && trimmed.length < 60)) {
            _writeParagraph(trimmed, 9.5, true);
          } else {
            _writeParagraph(trimmed, 9.5, false);
          }
          li++;
        }
      }
      y += 8; /* end-of-section → next title */
    }

    /* ── Cover ── */
    _addHeader();
    pdf.setFillColor(BRAND[0], BRAND[1], BRAND[2]);
    pdf.rect(0, 36, pageWidth, 64, 'F');
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(22); pdf.setTextColor(255, 255, 255);
    _pdfText('SCOPE OF WORK', marginL, 70);
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10.5);
    _pdfText('Commercial Cleaning Services Specification', marginL, 88);
    _drawStatusBadge(pageWidth - marginR, 70);
    y = 118;

    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15); pdf.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
    _pdfText(doc.facilityName || 'Facility', marginL, y);
    y += 14;
    _drawExecCard();

    /* ── Sections ── */
    doc.sections.forEach(function (section, idx) {
      if (idx === 14) {
        if (y + 290 > pageHeight - marginB) _newPage();
      }
      _sectionTitle(section.title);

      if (idx === 1) {
        var laborSplit = section.content.split(/\n\nEstimated cleaning labor/);
        var hourBlocks = String(laborSplit[0] || '').split(/\n\s*\n/);
        hourBlocks.forEach(function (block, bIdx) {
          var b = String(block || '').trim();
          if (!b) return;
          if (bIdx > 0) y += 7;
          b.split('\n').forEach(function (line) {
            var trimmed = line.trim();
            if (!trimmed) return;
            if (trimmed.charAt(0) === '\u2022') _writeBullets([trimmed]);
            else _writeParagraph(trimmed, 9.5, false);
          });
        });
        y += 6;
        _drawLaborCallout(doc.labor);
        y += 8;
        return;
      }
      if (idx === 2) {
        /* Keep intro + table header + ≥2 rows together — avoid page-1 void */
        _ensureSpace(9.5 * 1.42 * 3 + 20 + 22 * 2 + 28);
        _writeParagraph('The following areas are included in the scope of recurring cleaning services. Square footages are approximate and may be verified by Contractor during a pre-bid walk-through.', 9.5);
        y += 4;
        var areasPdf = doc.areas || _state.areas || [];
        _drawAreasTable(areasPdf);
        var facilitySqft = Number(doc.totalSqft) || Number(_state.totalSqft) || 0;
        var floorsPdf = Number(doc.numFloors || _state.numFloors) || 1;
        var areasSumPdf = typeof sumAreaSqft === 'function' ? sumAreaSqft(areasPdf) : 0;
        var balPdf = typeof sqftBalance === 'function'
          ? sqftBalance({ totalSqft: facilitySqft, areas: areasPdf })
          : { mismatch: false };
        var reconcile = 'Total facility: approximately ' +
          (facilitySqft ? facilitySqft.toLocaleString() : 'to be confirmed') +
          ' sq ft across ' + floorsPdf + ' floor' + (floorsPdf !== 1 ? 's' : '') + '.';
        if (areasSumPdf > 0) {
          reconcile += ' Sum of listed areas: ' + areasSumPdf.toLocaleString() + ' sq ft.';
        }
        _writeParagraph(reconcile, 9.5);
        if (balPdf.mismatch) {
          y += 2;
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(153, 27, 27);
          var warnLines = pdf.splitTextToSize(
            'WARNING: Area line items do not reconcile to the facility total (' +
            Math.abs(balPdf.delta || 0).toLocaleString() +
            ' sq ft off). Verify sq ft before bidding — labor staffing uses the facility total unless you update it.',
            contentWidth
          );
          _ensureSpace(warnLines.length * 13 + 8);
          pdf.text(warnLines, marginL, y);
          y += warnLines.length * 12 + 4;
          pdf.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
        }
        var missingFxPdf = typeof restroomsMissingFixtures === 'function' ? restroomsMissingFixtures(areasPdf) : [];
        if (missingFxPdf.length) {
          y += 2;
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(9.5);
          pdf.setTextColor(153, 27, 27);
          var fxLines = pdf.splitTextToSize(
            'NOTE: Restroom fixture counts are unpopulated for ' + missingFxPdf.join(', ') +
            '. Fixture count drives restroom labor time — add counts before locking staffing.',
            contentWidth
          );
          _ensureSpace(fxLines.length * 12 + 6);
          pdf.text(fxLines, marginL, y);
          y += fxLines.length * 11 + 4;
          pdf.setTextColor(TEXT[0], TEXT[1], TEXT[2]);
        }
        y += 4;
        _writeParagraph('Areas not listed above are excluded from recurring service unless added by written addendum signed by both parties.', 9.5);
        y += 8;
        return;
      }
      if (idx === 14) {
        _writeParagraph('By signing below, the authorized representatives of both parties agree that this Scope of Work accurately reflects the services to be provided and the standards to be maintained.', 9.5);
        _drawSignatureBlock(doc.facilityName || '');
        _drawExhibitsNote();
        return;
      }
      _writeSectionBody(section);
    });

    /* Single footer pass with final page count (no mid-stream footers) */
    var totalPages = pdf.internal.getNumberOfPages();
    for (var p = 1; p <= totalPages; p++) {
      pdf.setPage(p);
      pageNum = p;
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, pageHeight - 56, pageWidth, 56, 'F');
      _addFooter(totalPages);
    }

    var slug = slugify(doc.facilityName);
    var stamp = (typeof window.OporaFileStamp === 'function')
      ? window.OporaFileStamp()
      : new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    pdf.save('scope-of-work-' + slug + '-' + stamp + '.pdf');
    _trackSow('pdf_download_clicked', {
      facility_type: _state.facilityType || '',
      num_areas: (_state.areas || []).length
    });
    try {
      if (typeof gtag === 'function') gtag('event', 'sow_pdf_download', { tool: 'scope-of-work-generator', engine: 'jspdf-v3' });
    } catch (e0) {}
    try {
      var ping = {
        facility_type: _state.facilityType,
        total_sqft_bucket: sqftBucket(Number(_state.totalSqft) || 0),
        num_areas: (_state.areas || []).length,
        quality_level: _state.qualityLevel || 'APPA 2',
        timestamp: new Date().toISOString()
      };
      fetch('/apps/proxy/scope-events', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(ping), keepalive: true }).catch(function () {});
    } catch (e) {}
    return Promise.resolve(true);
  };

  /* Dock Download PDF → email gate first (skip if we already have a lead email) */
  SG.downloadPDFAndConvert = function () {
    var cached = _getRememberedLeadEmail();
    if (cached) {
      try {
        _trackSow('pdf_email_gate_skipped', { facility_type: _state.facilityType || '', reason: 'cached_lead_email' });
      } catch (eSkip) {}
      _toast('Downloading PDF…', 'success');
      SG.openConvertModal(2);
      SG.showConvertStep(2, { saved: true });
      SG.getPdfDocument(cached).then(function (parts) {
        var emailRes = parts && parts[1];
        if (emailRes && (emailRes.delivery === 'failed' || emailRes.success === false)) {
          SG.showConvertStep(2, { saved: false });
          if (emailRes.mailto) {
            _toast(
              'PDF downloaded. Couldn\u2019t email automatically — <a href="' +
                String(emailRes.mailto).replace(/"/g, '') +
                '">Open in email app</a>',
              'info'
            );
          } else {
            _toast('PDF downloaded. Couldn\u2019t email automatically.', 'info');
          }
          return;
        }
        SG.showConvertStep(2, { saved: true });
      }).catch(function () {
        SG.showConvertStep(2, { saved: false });
      });
      return;
    }
    SG.openConvertModal(1);
    try {
      _trackSow('pdf_email_gate_opened', { facility_type: _state.facilityType || '' });
    } catch (eG) {}
  };

  SG.copyAsText = function () {
    if (!SG._lastDoc) _renderPreview();
    var text = docToPlainText(SG._lastDoc);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(function () {
        _toast('Scope of work copied to clipboard', 'success');
      }).catch(function () { _fallbackCopy(text); });
    } else { _fallbackCopy(text); }
  };

  function _fallbackCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;top:-999px;left:-999px;opacity:0;';
    document.body.appendChild(ta); ta.focus(); ta.select();
    try { document.execCommand('copy'); _toast('Copied to clipboard', 'success'); }
    catch (e) { _toast('Copy failed — please select and copy manually', 'error'); }
    document.body.removeChild(ta);
  }

  SG.copyShareLink = function () {
    try {
      var fullState = JSON.parse(JSON.stringify(_state));
      var encoded = serializeState(fullState);
      var url = new URL(window.location.href);
      url.searchParams.set('sow', encoded);
      var link = url.toString();
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(link).then(function () {
          _toast('Share link copied to clipboard', 'success');
        });
      } else { _fallbackCopy(link); }
    } catch (e) { _toast('Could not generate share link', 'error'); }
  };

  SG.goToBidDrafter = function () {
    if (!SG._lastDoc) {
      _toast('Generate a preview first', 'error');
      return;
    }
    var doc = SG._lastDoc;
    var text = docToPlainText(doc);
    var st = _state || {};
    if (window.oporaPrefill && window.oporaPrefill.navigate) {
      window.oporaPrefill.navigate('/pages/bid-drafter', 'scope_of_work', {
        'opConvoInput': text.slice(0, 4000),
        'bd-sqft': st.totalSqft || ''
      });
    } else {
      window.location.href = '/pages/bid-drafter?from=scope_of_work&notes=' + encodeURIComponent(text.slice(0, 500));
    }
  };

  SG.showConvertStep = function (step, opts) {
    opts = opts || {};
    var modal = document.getElementById('sow-convert-modal');
    if (!modal) return;
    modal.querySelectorAll('[data-convert-step]').forEach(function (el) {
      var n = parseInt(el.getAttribute('data-convert-step'), 10);
      el.hidden = n !== step;
    });
    if (step === 2) {
      var eyebrow = modal.querySelector('[data-convert-step="2"] .sow-convert-modal__eyebrow');
      if (eyebrow) {
        eyebrow.textContent = opts.saved
          ? '✓ SOW saved to your inbox & library.'
          : 'PDF downloaded — continue when ready.';
        eyebrow.classList.toggle('sow-convert-modal__eyebrow--ok', !!opts.saved);
      }
    }
  };

  SG.openConvertModal = function (step) {
    var modal = document.getElementById('sow-convert-modal');
    if (!modal) return;
    var form = document.getElementById('sow-modal-email-form');
    if (form) form.hidden = false;
    var err = document.getElementById('sow-modal-email-err');
    if (err) err.hidden = true;
    var submit = document.getElementById('sow-modal-email-submit');
    if (submit) { submit.disabled = false; submit.textContent = 'Get PDF Document'; }
    var input = document.getElementById('sow-modal-email-input');
    if (input && !(input.value || '').trim()) {
      var remembered = _getRememberedLeadEmail();
      if (remembered) input.value = remembered;
    }
    SG.showConvertStep(step || 1);
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('sow-modal-open');
    if (input && (step || 1) === 1) setTimeout(function () { try { input.focus(); } catch (e) {} }, 80);
    try {
      if (typeof gtag === 'function') gtag('event', 'sow_convert_modal_open', { tool: 'scope-of-work-generator' });
    } catch (e0) {}
  };

  SG.closeConvertModal = function () {
    var modal = document.getElementById('sow-convert-modal');
    if (!modal) return;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    if (!document.getElementById('sow-save-modal') || document.getElementById('sow-save-modal').hidden) {
      document.documentElement.classList.remove('sow-modal-open');
    }
  };

  function _stageGuestDraftForLibrary() {
    _saveState();
    try { sessionStorage.setItem('opora_sow_awaiting_library_save', '1'); } catch (eS) {}
    var payload = null;
    try { payload = _buildSavePayload(); } catch (eP) { payload = null; }
    if (!payload) return null;
    if (window.OporaActionBar && typeof window.OporaActionBar.stageGuestSave === 'function') {
      window.OporaActionBar.stageGuestSave('scope-of-work-generator', payload);
    } else {
      try { _persistDraftToLibrary(); } catch (e2) {}
    }
    return payload;
  }

  function _completeLibrarySaveAfterAuth() {
    try { sessionStorage.removeItem('opora_sow_awaiting_library_save'); } catch (e) {}
    try { SG.closeSaveModal(); } catch (e2) {}
    try {
      if (window.OporaAuth && typeof window.OporaAuth.closeModal === 'function') {
        window.OporaAuth.closeModal();
      }
    } catch (e3) {}
    return SG.saveDraft().then(function (ok) {
      if (ok) _paintDockSaved();
      return ok;
    });
  }

  /* Guest Save → in-place Clerk modal over the builder (no page reload) */
  SG.openSaveModal = function () {
    if (window.Clerk && window.Clerk.user) {
      return SG.saveDraft();
    }
    _stageGuestDraftForLibrary();
    try {
      if (typeof gtag === 'function') gtag('event', 'sow_save_modal_open', { tool: 'scope-of-work-generator' });
    } catch (e0) {}

    if (window.OporaAuth && typeof window.OporaAuth.openModal === 'function') {
      window.OporaAuth.openModal({
        intent: 'sign-up',
        inPlace: true,
        title: 'Save this Scope of Work',
        tagline: 'Create a free account to edit, duplicate, and access this SOW across devices.',
        onAuthed: function () {
          _completeLibrarySaveAfterAuth();
        }
      });
      return;
    }

    /* Fallback shell if OporaAuth script missing */
    var modal = document.getElementById('sow-save-modal');
    if (!modal) return SG.continueGuestSaveAuth();
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('sow-modal-open');
    var cta = document.getElementById('sow-save-google');
    if (cta) setTimeout(function () { try { cta.focus(); } catch (e) {} }, 80);
  };

  SG.closeSaveModal = function () {
    var modal = document.getElementById('sow-save-modal');
    if (!modal) return;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    var convert = document.getElementById('sow-convert-modal');
    if (!convert || convert.hidden) document.documentElement.classList.remove('sow-modal-open');
  };

  /* Last-resort: full-page dashboard sign-up (only if in-place auth unavailable) */
  SG.continueGuestSaveAuth = function () {
    _stageGuestDraftForLibrary();
    var payload = null;
    try { payload = _buildSavePayload(); } catch (eP) { payload = null; }
    if (payload && window.OporaActionBar && typeof window.OporaActionBar.guestSaveThenAuth === 'function') {
      window.OporaActionBar.guestSaveThenAuth('scope-of-work-generator', payload, 'sign-up', { returnToTool: true });
      return Promise.resolve(false);
    }
    try {
      if (window.OporaEdge && typeof window.OporaEdge.stashAuthReturn === 'function') {
        window.OporaEdge.stashAuthReturn();
      }
    } catch (eA) {}
    var url = (window.OporaEdge && typeof window.OporaEdge.dashboardAuthUrl === 'function')
      ? window.OporaEdge.dashboardAuthUrl('sign-up')
      : 'https://dashboard.oporasupply.com/?intent=sign-up';
    window.location.href = url;
    return Promise.resolve(false);
  };
  SG.getSavePayload = _buildSavePayload;

  function _paintDockSaved() {
    var saveBtn = document.getElementById('sow-dock-save');
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><polyline points="20 6 9 17 4 12"/></svg><span class="opora-ab__label">Saved to Library</span>';
      setTimeout(function () {
        saveBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg><span class="opora-ab__label">Save to Library</span>';
      }, 2800);
    }
    _setDockStatus('Cloud Synced');
    var status = document.getElementById('sow-dock-status');
    if (status) status.title = 'This SOW is saved to your Opora library';
  }

  function _maybeCompleteLibrarySaveAfterAuth() {
    var awaiting = false;
    try { awaiting = sessionStorage.getItem('opora_sow_awaiting_library_save') === '1'; } catch (e) {}
    if (!awaiting) return;
    if (!(window.Clerk && window.Clerk.user)) return;
    _completeLibrarySaveAfterAuth();
  }

  function _workerBase() {
    return ((window.OporaEdge && window.OporaEdge.WORKER_URL) || window.OPORA_WORKER_URL || 'https://api.oporasupply.com').replace(/\/$/, '');
  }

  function _escapeHtml(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function _getRememberedLeadEmail() {
    if (window.OporaLeadEmail && typeof window.OporaLeadEmail.get === 'function') {
      return window.OporaLeadEmail.get() || '';
    }
    if (window.OporaActionBar && typeof window.OporaActionBar.getLeadEmail === 'function') {
      return window.OporaActionBar.getLeadEmail() || '';
    }
    try {
      var v = localStorage.getItem('opora_lead_email') || localStorage.getItem('opora_sow_lead_email') || '';
      v = String(v || '').trim().toLowerCase();
      return (v && v.indexOf('@') !== -1) ? v : '';
    } catch (e) { return ''; }
  }

  function _rememberLeadEmail(email) {
    if (window.OporaLeadEmail && typeof window.OporaLeadEmail.remember === 'function') {
      return window.OporaLeadEmail.remember(email) || '';
    }
    if (window.OporaActionBar && typeof window.OporaActionBar.rememberLeadEmail === 'function') {
      return window.OporaActionBar.rememberLeadEmail(email) || '';
    }
    var e = String(email || '').trim().toLowerCase();
    if (!e || e.indexOf('@') === -1) return '';
    try {
      localStorage.setItem('opora_lead_email', e);
      localStorage.setItem('opora_sow_lead_email', e);
    } catch (eL) {}
    return e;
  }

  function _trackEmailCapture(email, name, method) {
    _rememberLeadEmail(email);
    try {
      var profile = { $email: email, sow_facility: name, tool: 'scope-of-work-generator' };
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['identify', profile]);
        window.klaviyo.push(['track', 'SOW Email Capture', {
          tool: 'scope-of-work-generator',
          facility_name: name,
          facility_type: _state.facilityType || '',
          method: method || 'sow',
          url: window.location.href
        }]);
      }
      if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push(['identify', profile]);
        window._learnq.push(['track', 'SOW Email Capture', { tool: 'scope-of-work-generator', method: method || 'sow' }]);
      }
    } catch (eK) {}
    try {
      if (typeof gtag === 'function') gtag('event', 'email_capture', { tool: 'scope-of-work-generator', method: method || 'sow' });
    } catch (eG) {}
    _trackSow('email_captured_crm', { method: method || 'sow' });
  }

  function _requestMagicLink(email, nextUrl) {
    var body = JSON.stringify({ email: email, next: nextUrl || _draftEditUrl() });
    var edge = window.OporaEdge;
    if (edge && typeof edge.fetchApi === 'function') {
      return edge.fetchApi('/api/auth/magic-link', { method: 'POST', body: body }).then(function (r) {
        return r.json().catch(function () { return {}; });
      }).catch(function () { return null; });
    }
    return fetch(_workerBase() + '/api/auth/magic-link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body
    }).then(function (r) { return r.json().catch(function () { return {}; }); }).catch(function () { return null; });
  }

  function _persistDraftToLibrary() {
    _saveState();
    var payload;
    try {
      payload = _buildSavePayload();
    } catch (eP) {
      return Promise.resolve({ local: true, library: false, signedIn: false });
    }
    var signedIn = !!(window.Clerk && window.Clerk.user);
    if (signedIn && window.OporaSave && typeof window.OporaSave.save === 'function') {
      return window.OporaSave.save('scope-of-work-generator', payload)
        .then(function () { return { local: true, library: true, signedIn: true }; })
        .catch(function () { return { local: true, library: false, signedIn: true }; });
    }
    /* Guest: stage pending artifact for library claim after auth (no forced redirect) */
    try {
      var api = _workerBase();
      var md = (payload && (payload.body_markdown || payload.content)) || '';
      if (!md) return Promise.resolve({ local: true, library: false, signedIn: false });
      return fetch(api + '/api/artifacts/pending', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tool: 'scope-of-work-generator',
          tool_slug: 'scope-of-work-generator',
          title: (payload && payload.title) || '',
          body_markdown: md,
          content: md,
          input_payload: (payload && payload.input_payload) || {},
          metadata: (payload && payload.metadata) || {}
        })
      }).then(function (r) { return r.json(); }).then(function (j) {
        var token = j && (j.token || (j.data && j.data.token));
        if (token) {
          try {
            document.cookie = 'opora_pending_token=' + encodeURIComponent(token) +
              '; path=/; domain=.oporasupply.com; max-age=3600; SameSite=Lax; Secure';
          } catch (eC) {}
          return { local: true, library: true, signedIn: false };
        }
        return { local: true, library: false, signedIn: false };
      }).catch(function () { return { local: true, library: false, signedIn: false }; });
    } catch (e2) {
      return Promise.resolve({ local: true, library: false, signedIn: false });
    }
  }

  SG.saveDraft = function () {
    /* Save to Library — cloud/account persist (local auto-save is separate) */
    _saveState();
    var signedIn = !!(window.Clerk && window.Clerk.user);

    /* Guest: in-place auth modal over the builder */
    if (!signedIn) {
      SG.openSaveModal();
      return Promise.resolve(false);
    }

    _setDockStatus('Saving to library…');
    return _persistDraftToLibrary().then(function (res) {
      res = res || { local: true, library: false, signedIn: true };
      if (res.library && res.signedIn) {
        _toast('Saved to your Opora library', 'success');
        _setDockStatus('Cloud Synced');
        return true;
      }
      _toast('Couldn’t reach library — draft is still auto-saved here', 'error');
      _setDockStatus('Draft Auto-saved');
      return false;
    }).catch(function () {
      _toast('Couldn’t reach library — draft is still auto-saved here', 'error');
      _setDockStatus('Draft Auto-saved');
      return false;
    });
  };

  SG.goToBidGenerator = function () {
    _trackSow('bid_gen_handoff', { from: 'sow_post_download' });
    var st = _state || {};
    var labor = null;
    try {
      if (typeof estimateLaborFromProduction === 'function' && st.areas) {
        labor = estimateLaborFromProduction(st);
      }
    } catch (eL) {}
    var hours = labor && (labor.weekly_hours || labor.nightly_hours);
    if (window.oporaPrefill && window.oporaPrefill.navigate) {
      window.oporaPrefill.navigate('/pages/bid-generator', 'scope_of_work', {
        square_footage: st.totalSqft || '',
        facility_type: st.facilityType || '',
        quality_level: st.qualityLevel || '',
        weekly_hours: hours || ''
      });
    } else {
      var q = new URLSearchParams();
      if (st.totalSqft) q.set('sqft', String(st.totalSqft));
      if (st.facilityType) q.set('facility_type', String(st.facilityType));
      window.location.href = '/pages/bid-generator' + (q.toString() ? ('?' + q.toString()) : '');
    }
  };

  function _postSendEmail(body) {
    var json = JSON.stringify(body);
    var edge = window.OporaEdge;
    if (edge && typeof edge.fetchApi === 'function') {
      return edge.fetchApi('/api/send-email', { method: 'POST', body: json }).then(function (r) {
        return r.json().then(function (j) {
          if (!r.ok || (j && j.success === false)) {
            var err = new Error((j && j.error) || 'Email could not be sent');
            err.status = r.status;
            throw err;
          }
          return j;
        });
      });
    }
    var bases = [_workerBase()];
    if (window.OPORA_WORKER_URL_FALLBACK) bases.push(String(window.OPORA_WORKER_URL_FALLBACK).replace(/\/$/, ''));
    function tryBase(i) {
      if (i >= bases.length) return Promise.reject(new Error('Email could not be sent'));
      return fetch(bases[i] + '/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: json
      }).then(function (r) {
        return r.json().then(function (j) {
          if (!r.ok || (j && j.success === false)) {
            var err = new Error((j && j.error) || 'Email could not be sent');
            err.status = r.status;
            throw err;
          }
          return j;
        });
      }).catch(function () { return tryBase(i + 1); });
    }
    return tryBase(0);
  }

  SG.emailScopeCopy = function (toEmail, method) {
    var email = String(toEmail || '').trim();
    if (!email || email.indexOf('@') === -1) return Promise.reject(new Error('Enter a valid email'));
    if (!SG._lastDoc) _renderPreview();
    var name = (_state.facilityName && _state.facilityName.trim()) ? _state.facilityName.trim() : 'Your Facility';
    var link = _draftEditUrl();
    var sqft = _state.totalSqft ? (Number(_state.totalSqft).toLocaleString() + ' sq ft') : 'sq ft TBD';
    var subject = 'Your Scope of Work — ' + name;
    /* Inbox artifact: edit/magic link (+ PDF already downloaded in browser).
       Worker send-email has no attachments today — link is the durable reopen path. */
    var text =
      'Your scope of work for ' + name + ' is ready.\n\n' +
      'Facility: ' + name + '\n' +
      'Type: ' + (_state.facilityType || 'commercial') + '\n' +
      'Size: ' + sqft + '\n\n' +
      'Reopen & edit this draft (1-click):\n' + link + '\n\n' +
      'Your PDF also downloaded in the browser. We saved this draft to your free Opora library.\n\n' +
      '— Opora Supply · Not legal advice; review with counsel before signature.';
    var html =
      '<div style="font-family:Inter,Segoe UI,sans-serif;font-size:14px;line-height:1.55;color:#28251d">' +
      '<p>Your scope of work for <strong>' + _escapeHtml(name) + '</strong> is ready.</p>' +
      '<p style="color:#6f6d66">' + _escapeHtml(_state.facilityType || 'commercial') + ' · ' + _escapeHtml(sqft) + '</p>' +
      '<p><a href="' + _escapeHtml(link) + '" style="color:#01696F;font-weight:700">Reopen &amp; edit this draft →</a></p>' +
      '<p style="color:#6f6d66;font-size:12px">PDF downloaded in your browser · Draft saved to your free Opora library.</p>' +
      '<p style="color:#6f6d66;font-size:12px;margin-top:18px">Generated with Opora Supply · Not legal advice — review with counsel before signature.</p>' +
      '</div>';
    var body = {
      to: email,
      subject: subject,
      text: text,
      html: html,
      source_tool: 'scope-of-work-generator'
    };

    return _postSendEmail(body).then(function (j) {
      _trackEmailCapture(email, name, method || 'sow');
      return j;
    }).catch(function (err) {
      // Never auto-launch mailto / Outlook — caller may offer an explicit secondary link.
      _trackEmailCapture(email, name, (method || 'sow') + '_mailto_ready');
      var href = 'mailto:' + encodeURIComponent(email) +
        '?subject=' + encodeURIComponent(subject) +
        '&body=' + encodeURIComponent(text.slice(0, 1800));
      return {
        success: false,
        delivery: 'failed',
        mailto: href,
        fallback_error: (err && err.message) || 'Email could not be sent'
      };
    });
  };

  SG.sendAndSave = function (toEmail) {
    var email = String(toEmail || '').trim();
    var link = _draftEditUrl();
    return SG.emailScopeCopy(email, 'sow_modal').then(function (emailRes) {
      return Promise.all([
        _persistDraftToLibrary(),
        _requestMagicLink(email, link)
      ]).then(function () {
        return emailRes;
      });
    });
  };

  /* Email gate submit: PDF download starts immediately, then inbox + library */
  SG.getPdfDocument = function (toEmail) {
    var pdfP;
    try { pdfP = SG.downloadPDF(); } catch (eDl) { pdfP = Promise.resolve(false); }
    return Promise.all([
      Promise.resolve(pdfP).catch(function () { return false; }),
      SG.sendAndSave(toEmail)
    ]);
  };

  function _bindConvertModal() {
    var modal = document.getElementById('sow-convert-modal');
    if (!modal || modal.dataset.bound === '1') return;
    modal.dataset.bound = '1';
    modal.querySelectorAll('[data-sow-modal-close]').forEach(function (btn) {
      btn.addEventListener('click', function () { SG.closeConvertModal(); });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal && !modal.hidden) SG.closeConvertModal();
      var saveModal = document.getElementById('sow-save-modal');
      if (e.key === 'Escape' && saveModal && !saveModal.hidden) SG.closeSaveModal();
    });
    var bidBtn = document.getElementById('sow-modal-bid-gen');
    if (bidBtn) {
      bidBtn.addEventListener('click', function (e) {
        e.preventDefault();
        SG.closeConvertModal();
        SG.goToBidGenerator();
      });
    }
    var form = document.getElementById('sow-modal-email-form');
    if (form && form.dataset.bound !== '1') {
      form.dataset.bound = '1';
      var err = document.getElementById('sow-modal-email-err');
      var submit = document.getElementById('sow-modal-email-submit');
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var input = document.getElementById('sow-modal-email-input');
        var addr = input ? input.value : '';
        if (err) err.hidden = true;
        if (submit) { submit.disabled = true; submit.textContent = 'Building PDF…'; }
        var pdfP;
        try { pdfP = SG.downloadPDF(); } catch (eDl) { pdfP = Promise.resolve(false); }
        if (submit) submit.textContent = 'Sending…';
        Promise.all([
          Promise.resolve(pdfP).catch(function () { return false; }),
          SG.sendAndSave(addr)
        ]).then(function (parts) {
          var emailRes = parts && parts[1];
          if (submit) { submit.disabled = false; submit.textContent = 'Get PDF Document'; }
          if (emailRes && (emailRes.delivery === 'failed' || emailRes.success === false)) {
            if (err) {
              err.hidden = false;
              err.innerHTML =
                'Couldn\u2019t email automatically. ' +
                (emailRes.mailto
                  ? '<a href="' + String(emailRes.mailto).replace(/"/g, '') + '">Open in email app</a> \u2014 '
                  : '') +
                'your PDF still downloaded.';
            }
            setTimeout(function () {
              if (modal && !modal.hidden) SG.showConvertStep(2, { saved: false });
            }, 2200);
            return;
          }
          SG.showConvertStep(2, { saved: true });
        }).catch(function () {
          if (err) {
            err.hidden = false;
            err.textContent = 'Couldn\u2019t send. Check the address and try again — your PDF still downloaded.';
          }
          if (submit) { submit.disabled = false; submit.textContent = 'Get PDF Document'; }
          setTimeout(function () {
            if (modal && !modal.hidden) SG.showConvertStep(2, { saved: false });
          }, 1600);
        });
      });
    }
  }

  function _bindSaveModal() {
    var modal = document.getElementById('sow-save-modal');
    if (!modal || modal.dataset.bound === '1') return;
    modal.dataset.bound = '1';
    modal.querySelectorAll('[data-sow-save-close]').forEach(function (btn) {
      btn.addEventListener('click', function () { SG.closeSaveModal(); });
    });
    var googleBtn = document.getElementById('sow-save-google');
    if (googleBtn && googleBtn.dataset.bound !== '1') {
      googleBtn.dataset.bound = '1';
      googleBtn.addEventListener('click', function () {
        googleBtn.disabled = true;
        var label = googleBtn.querySelector('span');
        if (label) label.textContent = 'Continuing…';
        try {
          if (typeof gtag === 'function') gtag('event', 'sow_save_google_click', { tool: 'scope-of-work-generator' });
        } catch (eG) {}
        SG.continueGuestSaveAuth();
      });
    }
  }

  function _mountSowActionBar(attempt) {
    attempt = attempt || 0;
    if (!window.OporaActionBar || typeof window.OporaActionBar.mountSow !== 'function') {
      if (attempt < 20) setTimeout(function () { _mountSowActionBar(attempt + 1); }, 50);
      return;
    }
    try {
      window.OporaActionBar.mountSow({
        copyAsText: SG.copyAsText,
        saveDraft: SG.saveDraft,
        openGuestSaveModal: SG.openSaveModal,
        getSavePayload: _buildSavePayload,
        downloadPDFSignedIn: SG.downloadPDF,
        downloadPDFGuest: SG.downloadPDFAndConvert,
        /* Fallback if older actionbar build is cached */
        downloadPDF: function () {
          if (window.Clerk && window.Clerk.user) return SG.downloadPDF();
          return SG.downloadPDFAndConvert();
        }
      });
      /* Reparent dock to <body> so position:fixed is never trapped by tool-card transforms */
      var dock = document.getElementById('sow-float-dock');
      if (dock && dock.parentNode !== document.body) {
        document.body.appendChild(dock);
      }
      _bindConvertModal();
      _bindSaveModal();
      _maybeCompleteLibrarySaveAfterAuth();
    } catch (e) { /* never break wizard */ }
  }

  function _buildSavePayload() {
    if (!_state.frequencyMatrix) _state.frequencyMatrix = defaultFrequencyMatrix(_state.facilityType);
    if (!_state.areas) _state.areas = defaultAreasForFacilityType(_state.facilityType);
    var exclTextarea = document.getElementById('sow-exclusions-text');
    if (exclTextarea) _state.exclusionsList = exclTextarea.value;
    var stateForDoc = Object.assign({}, _state, {
      exclusions: _state.exclusionsList ? _state.exclusionsList.split('\n').map(function (l) { return l.trim(); }).filter(Boolean) : null
    });
    var doc = buildScopeDocument(stateForDoc);
    var text = docToPlainText(doc);
    return {
      title: 'Scope of Work — ' + ((_state.facilityName || '').trim() || 'Facility'),
      body_markdown: text,
      content: text,
      input_payload: {
        square_footage: _state.totalSqft,
        facility_type: _state.facilityType,
        num_floors: _state.numFloors,
        quality_level: _state.qualityLevel
      },
      metadata: { kind: 'scope_of_work', source: 'sow_wizard' }
    };
  }

  /**
   * Quick generate: short form → hydrate wizard defaults → Step 5 preview + PDF dock.
   * Same document path as the step-by-step wizard (no separate AI markdown).
   */
  SG.quickGenerateFromForm = function () {
    var errEl = document.getElementById('sow-quick-err');
    function showQuickErr(msg) {
      if (!errEl) return;
      errEl.hidden = false;
      errEl.textContent = msg;
    }
    function clearQuickErr() {
      if (!errEl) return;
      errEl.hidden = true;
      errEl.textContent = '';
    }
    clearQuickErr();

    var facilityName = String(_getVal('sow-quick-facility-name') || '').trim();
    var facilityType = _getVal('sow-quick-facility-type') || 'office';
    var rawSqft = _getVal('sow-quick-total-sqft');
    var parsedSqft = parseAreaSqft(rawSqft);
    var numFloors = _getVal('sow-quick-num-floors') || '1';
    var hoursOp = String(_getVal('sow-quick-hours-op') || '').trim() || 'Monday–Friday, 8:00 AM – 6:00 PM';
    var accessReq = String(_getVal('sow-quick-access-req') || '').trim();

    if (!facilityName) {
      showQuickErr('Enter a facility name.');
      return;
    }
    if (!String(rawSqft == null ? '' : rawSqft).trim()) {
      showQuickErr('Enter total square footage.');
      return;
    }
    if (!(parsedSqft > 0)) {
      showQuickErr('Total square footage must be a number greater than zero.');
      return;
    }
    if (!numFloors || Number(numFloors) <= 0) {
      showQuickErr('Number of floors must be at least 1.');
      return;
    }

    _state.facilityName = facilityName;
    _state.facilityType = facilityType;
    _state.totalSqft = String(Math.round(parsedSqft));
    _state.numFloors = String(numFloors);
    _state.hoursOfOperation = hoursOp;
    _state.accessRequirements = accessReq;
    _state.areas = defaultAreasForFacilityType(facilityType);
    try { autoScaleAreasToFacility(_state.areas, _state.totalSqft); } catch (eScale) { /* keep unscaled defaults */ }
    _state.frequencyMatrix = defaultFrequencyMatrix(facilityType);
    _state.frequencyMatrix = filterTasksForSurfaces(_state.frequencyMatrix, _state.areas || []);
    _state._tasksNeedSurfaceSync = false;
    _state.qualityLevel = _state.qualityLevel || 'APPA 2';
    if (!_state.insuranceGL) _state.insuranceGL = '$1,000,000';
    if (!_state.insuranceAuto) _state.insuranceAuto = '$1,000,000';
    if (!_state.insuranceWC) _state.insuranceWC = '$500,000';
    if (!_state.insuranceUmbrella) _state.insuranceUmbrella = '1000000';
    if (!_state.contractTerm) _state.contractTerm = '1 year with renewal options';
    if (!_state.escalationCap) _state.escalationCap = '3';
    _state.additionalInsured = facilityName;
    _state.exclusionsList = _normalizeExclusionsText(_state.exclusionsList || _defaultExclusionsText());
    _state.step = 5;

    var resumeBanner = document.getElementById('sow-resume-banner');
    if (resumeBanner) resumeBanner.style.display = 'none';

    if (typeof window.OporaSowSetMode === 'function') {
      window.OporaSowSetMode('wizard');
    }

    _populateFormFromState();
    try { _renderAreas(); } catch (eA) { /* preview builds from state */ }
    try { _renderFrequencyMatrix(); } catch (eF) { /* preview builds from state */ }
    _clearStepErrors();
    _trackSow('quick_generate', {
      facility_type: facilityType,
      total_sqft: _state.totalSqft,
      num_floors: _state.numFloors
    });
    _showStep(5);
  };

  SG.startOver = function () {
    if (!window.confirm('Start over? This clears all fields and your saved draft.')) return;
    var resumeBanner = document.getElementById('sow-resume-banner');
    if (resumeBanner) resumeBanner.style.display = 'none';
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    _state.step = 1;
    _state.facilityName = '';
    _state.facilityType = 'office';
    _state.totalSqft = '';
    _state.numFloors = '1';
    _state.hoursOfOperation = 'Monday–Friday, 8:00 AM – 6:00 PM';
    _state.accessRequirements = '';
    _state.areas = null;
    _state.frequencyMatrix = null;
    _state.qualityLevel = 'APPA 2';
    _state.certifications = '';
    _state.insuranceGL = '$1,000,000';
    _state.insuranceAuto = '$1,000,000';
    _state.insuranceWC = '$500,000';
    _state.insuranceUmbrella = '1000000';
    _state.additionalInsured = '';
    _state.contractTerm = '1 year with renewal options';
    _state.escalationCap = '3';
    _state.exclusionsList = _defaultExclusionsText();
    _state.exhibits = null;
    _populateFormFromState();
    // populate skips empty→filled wipe for sqft; clear explicitly on start-over
    _setVal('sow-total-sqft', '');
    _setVal('sow-additional-insured', '');
    var resumeBanner2 = document.getElementById('sow-resume-banner');
    if (resumeBanner2) resumeBanner2.style.display = 'none';
    _clearStepErrors();
    _showStep(1);
  };

  SG.init = function () {
    var hasSaved = _loadState();
    if (hasSaved && _state.facilityType) {
      var resumeBanner = document.getElementById('sow-resume-banner');
      if (resumeBanner) {
        resumeBanner.style.display = '';
        var resumeBtn = document.getElementById('sow-resume-btn');
        var dismissBtn = document.getElementById('sow-resume-dismiss');
        if (resumeBtn) { resumeBtn.addEventListener('click', function () { resumeBanner.style.display = 'none'; _populateFormFromState(); _showStep(_state.step || 1); }); }
        if (dismissBtn) {
          dismissBtn.addEventListener('click', function () { SG.startOver(); });
        }
      }
    }
    _populateFormFromState();
    for (var i = 1; i <= MAX_STEP; i++) {
      (function (step) {
        var btn = _stepBtn(step);
        if (!btn) return;
        btn.addEventListener('click', function () {
          if (Number(step) === Number(_state.step)) return;
          _syncStateFromStep(_state.step);
          _clearStepErrors();
          _showStep(step);
        });
      })(i);
    }
    document.querySelectorAll('[data-sow-next]').forEach(function (btn) { btn.addEventListener('click', function () { _syncStateFromStep(_state.step); _advance(); }); });
    document.querySelectorAll('[data-sow-back]').forEach(function (btn) { btn.addEventListener('click', function () { _syncStateFromStep(_state.step); _back(); }); });
    var sqftInput = document.getElementById('sow-total-sqft');
    if (sqftInput) {
      sqftInput.addEventListener('input', function () {
        _state.totalSqft = String(sqftInput.value || '').trim();
        if (_state.totalSqft) _clearStepErrors();
      });
    }
    var ftSelect = document.getElementById('sow-facility-type');
    if (ftSelect) { ftSelect.addEventListener('change', function () { _state.facilityType = ftSelect.value; _state.areas = null; _state.frequencyMatrix = null; }); }
    var addAreaBtn = document.getElementById('sow-add-area');
    if (addAreaBtn) { addAreaBtn.addEventListener('click', function () { if (!_state.areas) _state.areas = defaultAreasForFacilityType(_state.facilityType); _state.areas.push({ name: '', sqft: '', fixtures: '', floorType: SG._floorTypeNames()[0] || 'Carpet — commercial loop', notes: '' }); _renderAreas(); }); }
    ['daily', 'weekly', 'monthly', 'quarterly', 'annual'].forEach(function (freq) {
      var btn = document.getElementById('sow-add-task-' + freq);
      if (!btn) return;
      btn.addEventListener('click', function () { if (!_state.frequencyMatrix) _state.frequencyMatrix = defaultFrequencyMatrix(_state.facilityType); _state.frequencyMatrix[freq].push(''); _renderFrequencyMatrix(); });
    });
    var dlPdf = document.getElementById('sow-download-pdf');
    if (dlPdf) {
      dlPdf.addEventListener('click', function () {
        if (window.Clerk && window.Clerk.user) SG.downloadPDF();
        else SG.downloadPDFAndConvert();
      });
    }
    var copyText = document.getElementById('sow-copy-text');
    if (copyText) copyText.addEventListener('click', SG.copyAsText);
    var shareLink = document.getElementById('sow-share-link');
    if (shareLink) shareLink.addEventListener('click', SG.copyShareLink);
    var startOverBtn = document.getElementById('sow-start-over');
    if (startOverBtn) startOverBtn.addEventListener('click', SG.startOver);
    var saveBtn = document.getElementById('sow-save-library');
    if (saveBtn && window.OporaSave && typeof window.OporaSave.mountButton === 'function') {
      window.OporaSave.mountButton(saveBtn, _buildSavePayload);
    }
    _bindConvertModal();
    _bindSaveModal();
    window.addEventListener('opora:signed-in', function () {
      _maybeCompleteLibrarySaveAfterAuth();
    });
    document.addEventListener('clerk:loaded', function () {
      _maybeCompleteLibrarySaveAfterAuth();
    });
    try {
      var u = new URL(window.location.href);
      if (u.searchParams.get('sow_return') === '1') {
        _trackSow('magic_link_login', { via: 'inbox_edit_link' });
        u.searchParams.delete('sow_return');
        var qs = u.searchParams.toString();
        window.history.replaceState(null, '', u.pathname + (qs ? '?' + qs : '') + u.hash);
      }
    } catch (eRet) {}
    _showStep(_state.step || 1);
    setTimeout(_maybeCompleteLibrarySaveAfterAuth, 400);
  };

  function _populateFormFromState() {
    _setVal('sow-facility-name', _state.facilityName);
    _setVal('sow-facility-type', _state.facilityType);
    // Don't wipe a live/prefilled sqft value with empty draft state
    if (_state.totalSqft !== '' && _state.totalSqft != null) {
      _setVal('sow-total-sqft', parseAreaSqft(_state.totalSqft) || _state.totalSqft);
    }
    _setVal('sow-num-floors', _state.numFloors);
    _setVal('sow-hours-op', _state.hoursOfOperation);
    _setVal('sow-access-req', _state.accessRequirements);
    _setVal('sow-quality-level', _state.qualityLevel);
    _setVal('sow-certifications', _state.certifications);
    _setVal('sow-insurance-gl', _state.insuranceGL);
    _setVal('sow-insurance-auto', _state.insuranceAuto);
    _setVal('sow-insurance-wc', _state.insuranceWC);
    _setVal('sow-insurance-umbrella', _state.insuranceUmbrella || '1000000');
    _setVal('sow-additional-insured', _state.additionalInsured || '');
    _setVal('sow-contract-term', _state.contractTerm || '1 year with renewal options');
    _setVal('sow-escalation-cap', _state.escalationCap || '3');
    var exclSeed = _normalizeExclusionsText(_state.exclusionsList || _defaultExclusionsText());
    _state.exclusionsList = exclSeed;
    _setVal('sow-exclusions-text', exclSeed);
  }

  function _normalizeExclusionsText(text) {
    return String(text || '').replace(/\(strip\/wax,/g, '(strip-and-wax,');
  }

  function _defaultExclusionsText() {
    return [
      'Supply of restroom consumables (paper products, soap, liners) unless separately quoted',
      'Periodic floor restoration (strip-and-wax, carpet extraction) beyond quarterly schedule unless included in addendum',
      'Biohazard, sharps, or regulated medical waste handling',
      'Post-construction or post-renovation cleaning',
      'Exterior window cleaning above first floor',
      'Pest control treatment',
      'Laundry or linen services',
      'Moving or rearranging furniture heavier than 30 lbs'
    ].join('\n');
  }

  function _setVal(id, val) {
    var el = document.getElementById(id);
    if (!el || val === undefined || val === null) return;
    el.value = val;
  }

  function _syncStateFromStep(step) {
    var n = Number(step);
    if (n === 1) {
      _state.facilityName = _getVal('sow-facility-name');
      _state.facilityType = _getVal('sow-facility-type') || 'office';
      var rawSqft = _getVal('sow-total-sqft');
      var parsedSqft = parseAreaSqft(rawSqft);
      _state.totalSqft = parsedSqft > 0 ? String(Math.round(parsedSqft)) : String(rawSqft || '').trim();
      // Normalize commas / formatting into the number box so .value stays readable
      if (parsedSqft > 0 && String(rawSqft).trim() !== _state.totalSqft) {
        _setVal('sow-total-sqft', _state.totalSqft);
      }
      _state.numFloors = _getVal('sow-num-floors') || '1';
      _state.hoursOfOperation = _getVal('sow-hours-op');
      _state.accessRequirements = _getVal('sow-access-req');
    }
    if (n === 4) {
      _state.qualityLevel = _getVal('sow-quality-level') || 'APPA 2';
      _state.certifications = _getVal('sow-certifications');
      _state.insuranceGL = _getVal('sow-insurance-gl');
      _state.insuranceAuto = _getVal('sow-insurance-auto');
      _state.insuranceWC = _getVal('sow-insurance-wc');
      _state.insuranceUmbrella = _getVal('sow-insurance-umbrella') || '1000000';
      _state.additionalInsured = _getVal('sow-additional-insured');
      _state.contractTerm = _getVal('sow-contract-term') || '1 year with renewal options';
      _state.escalationCap = _getVal('sow-escalation-cap') || '3';
      _state.exclusionsList = _getVal('sow-exclusions-text');
    }
    if (n === 1 && Number(_state.step) === 1) { if (!_state.areas) { _state.areas = null; } }
  }

  function _getVal(id) {
    var el = document.getElementById(id);
    return el ? el.value : '';
  }

  SG._onShowStep = function (n) {
    if (n === 2) { _renderAreas(); }
    if (n === 3) { _renderFrequencyMatrix(); }
    if (n === 4) { _syncAdditionalInsuredDefault(); }
    if (n === 5) { _mountSowActionBar(); }
  };

  function _syncAdditionalInsuredDefault() {
    var input = document.getElementById('sow-additional-insured');
    if (!input) return;
    var facility = (_state.facilityName || _getVal('sow-facility-name') || '').trim();
    var current = String(input.value || '').trim();
    /* Prefill from facility name when empty; keep user overrides */
    if (!current && facility) {
      input.value = facility;
      _state.additionalInsured = facility;
    }
  }

  var _origShowStep = _showStep;
  _showStep = function (n) { _origShowStep(n); SG._onShowStep(n); };

  SG._getState = function () { return _state; };
  SG._setState = function (s) { Object.assign(_state, s); };

  global.OporaScopeGen = SG;

}(window));
} /* end: if (typeof window !== 'undefined') */

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    slugify: slugify,
    sqftBucket: sqftBucket,
    defaultQualityLevel: defaultQualityLevel,
    appaDescription: appaDescription,
    defaultAreasForFacilityType: defaultAreasForFacilityType,
    defaultFrequencyMatrix: defaultFrequencyMatrix,
    parseAreaSqft: parseAreaSqft,
    parseFixtures: parseFixtures,
    isRestroomArea: isRestroomArea,
    isStoneFloorType: isStoneFloorType,
    stoneAreaNames: stoneAreaNames,
    restroomsMissingFixtures: restroomsMissingFixtures,
    sumAreaSqft: sumAreaSqft,
    sqftBalance: sqftBalance,
    autoScaleAreasToFacility: autoScaleAreasToFacility,
    detectSurfaces: detectSurfaces,
    filterTasksForSurfaces: filterTasksForSurfaces,
    mixPctFromAreas: mixPctFromAreas,
    estimateLaborFromProduction: estimateLaborFromProduction,
    validateStep: validateStep,
    serializeState: serializeState,
    deserializeState: deserializeState,
    buildScopeDocument: buildScopeDocument,
    scrubBracketPlaceholders: scrubBracketPlaceholders,
    docToPlainText: docToPlainText
  };
}
